import { useEffect, useMemo, useRef, useState } from "react";
import { DateRangePicker } from "react-date-range";
import "react-date-range/dist/styles.css";
import "react-date-range/dist/theme/default.css";
import CalendarTodayIcon from "@mui/icons-material/CalendarToday";
import dayjs from "dayjs";
import { useSelector } from "react-redux";
import { Backdrop, CircularProgress } from "@mui/material";
import InsightsIcon from "@mui/icons-material/Insights";
import WarningAmberIcon from "@mui/icons-material/WarningAmber";
import BarChartIcon from "@mui/icons-material/BarChart";
import TimelineIcon from "@mui/icons-material/Timeline";
import AssignmentTurnedInIcon from "@mui/icons-material/AssignmentTurnedIn";
import HourglassEmptyIcon from "@mui/icons-material/HourglassEmpty";
import GroupIcon from "@mui/icons-material/Group";
import BusinessIcon from "@mui/icons-material/Business";
import RefreshIcon from "@mui/icons-material/Refresh";
import RemoveRedEyeIcon from "@mui/icons-material/RemoveRedEye";
import LocalShippingIcon from "@mui/icons-material/LocalShipping";
import AIFFormModal from "../../forms/viewAiF";
import ViewFormModal from "../../forms/ViewFormModal";
import WDSFormModal from "../../forms/WDSformModal";
import {
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  Tooltip,
  CartesianGrid,
  PieChart,
  Pie,
  Cell,
  Legend,
  LabelList,
  Treemap,
  LineChart,
  Line,
  AreaChart,
  Area,
} from "recharts";

import SideNavLayout from "../../Sidenav/SideNavLayout";
import api from "../../api/api";
import { HseStatsContent } from "../hsestats/HseStatsDashboard";

// ---------------------------------------------------------------------------
// Configuration
// ---------------------------------------------------------------------------

// Only these 4 form types power the analytics dashboard.
const FORM_TYPES = [
  { code: "BBS", label: "BBS Card", color: "#155DFC" },
  { code: "SHP", label: "Safety & Health", color: "#00A63E" },
  { code: "WDS", label: "Weekly Driver Log", color: "#E17100" },
  { code: "AIF", label: "Accident / Incident", color: "#E7000B" },
];
const FORM_CODES = FORM_TYPES.map((f) => f.code);
const TYPE_COLOR = Object.fromEntries(FORM_TYPES.map((f) => [f.code, f.color]));
const TYPE_LABEL = Object.fromEntries(FORM_TYPES.map((f) => [f.code, f.label]));

const TABS = [
  { key: "insights", label: "Key Insights", Icon: InsightsIcon },
  { key: "incidents", label: "Incidents", Icon: WarningAmberIcon },
  { key: "bbs", label: "BBS Data", Icon: BarChartIcon },
  { key: "driverlog", label: "Driver Log", Icon: LocalShippingIcon },
  { key: "trends", label: "Trends", Icon: TimelineIcon },
  { key: "kpis", label: "KPI's", Icon: InsightsIcon },
];

const RANGE_OPTIONS = [
  { key: "mtd", label: "This Month", days: null },
  { key: "30d", label: "Last 30 Days", days: 30 },
  { key: "90d", label: "Last 90 Days", days: 90 },
];

// ---------------------------------------------------------------------------
// Small UI primitives
// ---------------------------------------------------------------------------

const Card = ({ children, className = "" }) => (
  <div
    className={`rounded-2xl border border-gray-200 bg-white p-5 shadow-sm ${className}`}
  >
    {children}
  </div>
);

const SectionTitle = ({ title, subtitle, right = null }) => (
  <div className="flex items-start justify-between gap-2 mb-4">
    <div>
      <h2 className="text-base font-semibold text-[#0A0A0A] text-left">
        {title}
      </h2>
      {subtitle && (
        <p className="text-xs text-[#717182] text-left">{subtitle}</p>
      )}
    </div>
    {right}
  </div>
);

const StatCard = ({ label, value, sub, accent, Icon, trend }) => (
  <div
    className="flex-1 min-w-[180px] rounded-2xl border border-gray-200 bg-white p-4 shadow-sm relative overflow-hidden"
    style={{ border: `2px solid ${accent}` }}
  >
    <div className="flex items-center justify-between">
      <span className="text-xs font-medium uppercase tracking-wider text-gray-500">
        {label}
      </span>
      {Icon && (
        <span
          className="rounded-full p-1.5"
          style={{ background: `${accent}15`, color: accent }}
        >
          <Icon style={{ fontSize: 18 }} />
        </span>
      )}
    </div>
    <div className="mt-2 flex items-baseline gap-2 text-left">
      <span className="text-2xl font-bold text-[#0A0A0A]">{value}</span>
      {trend !== undefined && trend !== null && (
        <span
          className={`text-xs font-medium ${
            trend > 0
              ? "text-red-500"
              : trend < 0
              ? "text-green-600"
              : "text-gray-500"
          }`}
        >
          {trend > 0 ? "▲" : trend < 0 ? "▼" : "—"} {Math.abs(trend)}%
        </span>
      )}
    </div>
    {sub && <div className="text-xs text-gray-500 mt-1 text-left">{sub}</div>}
  </div>
);

const EmptyState = ({ message = "No data for the selected range." }) => (
  <div className="text-center py-10 text-sm text-gray-500">{message}</div>
);

// ---------------------------------------------------------------------------
// Computation helpers
// ---------------------------------------------------------------------------

const getRowDate = (row) => {
  if (row.created_at) return dayjs(row.created_at);
  if (row.filled_date) return dayjs(row.filled_date);
  return null;
};

const isCompleted = (row) => {
  if (row.form_type === "SHP") return row.pm_status === "Y";
  return row.status === "A";
};

// Group rows into N month buckets ending today.
const buildMonthlyTrend = (rows, months = 6) => {
  const now = dayjs();
  const buckets = [];
  for (let i = months - 1; i >= 0; i--) {
    const m = now.subtract(i, "month");
    buckets.push({
      key: m.format("YYYY-MM"),
      label: m.format("MMM YY"),
      BBS: 0,
      SHP: 0,
      WDS: 0,
      AIF: 0,
      total: 0,
    });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const d = getRowDate(r);
    if (!d) return;
    const k = d.format("YYYY-MM");
    if (idx[k] === undefined) return;
    if (!FORM_CODES.includes(r.form_type)) return;
    buckets[idx[k]][r.form_type] += 1;
    buckets[idx[k]].total += 1;
  });
  return buckets;
};

// Weekly buckets covering the last N weeks (default 12).
const buildWeeklyTrend = (rows, weeks = 12) => {
  const now = dayjs();
  const buckets = [];
  for (let i = weeks - 1; i >= 0; i--) {
    const weekStart = now.subtract(i, "week").startOf("week");
    buckets.push({
      key: weekStart.format("YYYY-MM-DD"),
      label: weekStart.format("MMM D"),
      BBS: 0,
      SHP: 0,
      WDS: 0,
      AIF: 0,
      total: 0,
    });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const d = getRowDate(r);
    if (!d) return;
    const k = d.startOf("week").format("YYYY-MM-DD");
    if (idx[k] === undefined) return;
    if (!FORM_CODES.includes(r.form_type)) return;
    buckets[idx[k]][r.form_type] += 1;
    buckets[idx[k]].total += 1;
  });
  return buckets;
};

// Monthly buckets covering Jan → current month of the current year.
const buildYearlyTrendCurrentYear = (rows) => {
  const start = dayjs().startOf("year");
  const today = dayjs();
  const months = today.diff(start, "month") + 1;
  const buckets = [];
  for (let i = 0; i < months; i++) {
    const m = start.add(i, "month");
    buckets.push({
      key: m.format("YYYY-MM"),
      label: m.format("MMM"),
      BBS: 0,
      SHP: 0,
      WDS: 0,
      AIF: 0,
      total: 0,
    });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const d = getRowDate(r);
    if (!d) return;
    const k = d.format("YYYY-MM");
    if (idx[k] === undefined) return;
    if (!FORM_CODES.includes(r.form_type)) return;
    buckets[idx[k]][r.form_type] += 1;
    buckets[idx[k]].total += 1;
  });
  return buckets;
};

// Daily buckets covering the current month up to today.
const buildDailyTrendCurrentMonth = (rows) => {
  const start = dayjs().startOf("month");
  const today = dayjs();
  const days = today.diff(start, "day") + 1;
  const buckets = [];
  for (let i = 0; i < days; i++) {
    const d = start.add(i, "day");
    buckets.push({
      key: d.format("YYYY-MM-DD"),
      label: d.format("MMM D"),
      BBS: 0,
      SHP: 0,
      WDS: 0,
      AIF: 0,
      total: 0,
    });
  }
  const idx = Object.fromEntries(buckets.map((b, i) => [b.key, i]));
  rows.forEach((r) => {
    const d = getRowDate(r);
    if (!d) return;
    const k = d.format("YYYY-MM-DD");
    if (idx[k] === undefined) return;
    if (!FORM_CODES.includes(r.form_type)) return;
    buckets[idx[k]][r.form_type] += 1;
    buckets[idx[k]].total += 1;
  });
  return buckets;
};

const countBy = (rows, getter) => {
  const out = {};
  rows.forEach((r) => {
    const k = getter(r);
    if (!k) return;
    out[k] = (out[k] || 0) + 1;
  });
  return out;
};

// ---------------------------------------------------------------------------
// Tab content — each receives the filtered/dated rows
// ---------------------------------------------------------------------------

// Module-level Spanish→English label alias map (shared across components)
const BBS_LABEL_ALIASES = {
  "ojos y cara": "Eye & Face",
  mano: "Hand",
  cuerpo: "Body",
  cabeza: "Head",
  respiratorio: "Respiratory",
  audición: "Hearing",
  audicion: "Hearing",
  "posicionamiento corporal adecuado (línea de fuego)": "Proper Body Positioning (Line of fire)",
  "posicionamiento corporal adecuado (linea de fuego)": "Proper Body Positioning (Line of fire)",
  "posicionamiento adecuado del cuerpo (línea de fuego)": "Proper Body Positioning (Line of fire)",
  "posicionamiento adecuado del cuerpo (linea de fuego)": "Proper Body Positioning (Line of fire)",
  "mecánica de levantamiento manual": "Manual Lifting Mechanics",
  "mecanica de levantamiento manual": "Manual Lifting Mechanics",
  "levantamiento manual": "Manual Lifting Mechanics",
  "manual lifting mechanics": "Manual Lifting Mechanics",
  repetición: "Repetition",
  repeticion: "Repetition",
  "puntos de pellizco": "Pinch Points",
  "tirar/empujar": "Pulling/Pushing",
  "tirando/empujando": "Pulling/Pushing",
  extender: "Extending",
  extendiendo: "Extending",
  "resbalones, tropiezos, caídas": "Slips, Trips, Falls",
  "resbalones, tropiezos, caidas": "Slips, Trips, Falls",
  "resbalones, tropezones, caídas": "Slips, Trips, Falls",
  "resbalones, tropezones, caidas": "Slips, Trips, Falls",
  "área de trabajo congestionada": "Congested Work Area",
  "area de trabajo congestionada": "Congested Work Area",
  "barricadas/señalización": "Barricades/Signage",
  "barricadas/senalizacion": "Barricades/Signage",
  excavaciones: "Excavations",
  "protección contra caídas": "Fall Protection",
  "proteccion contra caidas": "Fall Protection",
  "control de derrames": "Spill Control",
  "tarea cubierta por jsa": "JSA Cover Task",
  "reconocimiento de peligros": "Hazard Recognition",
  "aislamiento de energía (loto)": "Energy Isolation (LOTO)",
  "aislamiento de energia (loto)": "Energy Isolation (LOTO)",
  "operaciones simultáneas (simops)": "Simultaneous Operations (SIMOPS)",
  "operaciones simultaneas (simops)": "Simultaneous Operations (SIMOPS)",
  "herramientas manuales/eléctricas": "Hand/Power Tools",
  "herramientas manuales/electricas": "Hand/Power Tools",
  "equipo móvil": "Mobile Equipment",
  "equipo movil": "Mobile Equipment",
};
const canonicalLabel = (raw) => {
  if (!raw) return raw;
  const key = String(raw).trim().toLowerCase();
  return BBS_LABEL_ALIASES[key] || raw;
};

const KeyInsights = ({ rows }) => {
  const [showBuckets, setShowBuckets] = useState(false);
  const [bucketDetail, setBucketDetail] = useState(null); // bucket name to open modal
  const [companies, setCompanies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/v1/timesheets/company-details");
        if (!cancelled && resp.status === 200 && Array.isArray(resp.data)) {
          setCompanies(resp.data);
        }
      } catch (e) {
        console.error("Failed to load companies", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const byType = useMemo(() => countBy(rows, (r) => r.form_type), [rows]);
  const pieData = FORM_TYPES.map((f) => ({
    name: f.label,
    code: f.code,
    value: byType[f.code] || 0,
    color: f.color,
  })).filter((d) => d.value > 0);

  const completed = rows.filter(isCompleted).length;
  const completionRate = rows.length
    ? Math.round((completed / rows.length) * 100)
    : 0;

  const byObserver = useMemo(
    () => countBy(rows, (r) => r.observer_name || r.emp_name),
    [rows],
  );
  const topObservers = Object.entries(byObserver)
    .sort((a, b) => b[1] - a[1])
    .slice(0, 6)
    .map(([name, count]) => ({ name, count }));

  // Safety Risk Areas — aggregate AIF incident locations + top causes/body parts.
  const riskAreas = useMemo(() => {
    const aif = rows.filter((r) => r.form_type === "AIF");
    const locByCount = {};
    const jobByCount = {};
    const companyByCount = {};
    const causes = {};
    const parts = {};
    aif.forEach((r) => {
      const m = r.incident_meta || {};
      const loc = m.project_location || "Unspecified";
      locByCount[loc] = (locByCount[loc] || 0) + 1;
      const job = r.jobDescription || "Unassigned job";
      jobByCount[job] = (jobByCount[job] || 0) + 1;
      const co = r.company_name || "Unknown company";
      companyByCount[co] = (companyByCount[co] || 0) + 1;
      if (m.incident_cause)
        causes[m.incident_cause] = (causes[m.incident_cause] || 0) + 1;
      (m.body_parts || []).forEach((p) => {
        parts[p] = (parts[p] || 0) + 1;
      });
    });
    const top = (m, n = 4) =>
      Object.entries(m)
        .sort((a, b) => b[1] - a[1])
        .slice(0, n)
        .map(([name, value]) => ({ name, value }));
    return {
      total: aif.length,
      locations: top(locByCount),
      jobs: top(jobByCount),
      companies: top(companyByCount),
      causes: top(causes, 3),
      parts: top(parts, 3),
    };
  }, [rows]);

  // BBS combined behavior breakdown — Safe vs Concern aggregated per bucket
  const bbsCombined = useMemo(() => {
    const bbs = rows.filter((r) => r.form_type === "BBS");
    const buckets = ["PPE", "Body Position", "Work Environment", "Work Procedure"];
    const colors = { PPE: "#155DFC", "Body Position": "#9333EA", "Work Environment": "#0EA5E9", "Work Procedure": "#F97316" };
    const data = buckets.map((b) => {
      let S = 0;
      let C = 0;
      const perItem = {};
      bbs.forEach((r) => {
        const arr = r.bbs_meta?.[b];
        if (!Array.isArray(arr)) return;
        arr.forEach((it) => {
          if (!it?.label) return;
          if (!perItem[it.label]) perItem[it.label] = { label: it.label, S: 0, C: 0 };
          if (it.answer === "S") {
            perItem[it.label].S += 1;
            S += 1;
          } else if (it.answer === "C") {
            perItem[it.label].C += 1;
            C += 1;
          }
        });
      });
      const items = Object.values(perItem);
      const safest = [...items].sort((a, b) => b.S - a.S)[0];
      const concern = [...items].sort((a, b) => b.C - a.C)[0];
      const total = S + C;
      return {
        name: b,
        S,
        C,
        total,
        safePct: total ? +((S / total) * 100).toFixed(1) : 0,
        concernPct: total ? +((C / total) * 100).toFixed(1) : 0,
        color: colors[b],
        safest: safest && safest.S > 0 ? safest : null,
        concern: concern && concern.C > 0 ? concern : null,
      };
    });
    return { data, totalForms: bbs.length };
  }, [rows]);

  // Per-item aggregation for the active bucket across ALL BBS forms / all companies
  const bucketDetailStats = useMemo(() => {
    if (!bucketDetail) return null;
    const bbs = rows.filter((r) => r.form_type === "BBS");
    const perItem = {};
    let totalS = 0;
    let totalC = 0;
    bbs.forEach((r) => {
      const arr = r.bbs_meta?.[bucketDetail];
      if (!Array.isArray(arr)) return;
      arr.forEach((it) => {
        if (!it?.label) return;
        const canon = canonicalLabel(it.label);
        if (!perItem[canon]) perItem[canon] = { label: canon, S: 0, C: 0 };
        if (it.answer === "S") {
          perItem[canon].S += 1;
          totalS += 1;
        } else if (it.answer === "C") {
          perItem[canon].C += 1;
          totalC += 1;
        }
      });
    });
    const total = totalS + totalC || 1;
    const items = Object.values(perItem)
      .map((it) => {
        const pctS = +((it.S / total) * 100).toFixed(1);
        const pctC = +((it.C / total) * 100).toFixed(1);
        return {
          ...it,
          pctS,
          pctC,
          sLabel: it.S ? `${it.S} (${pctS}%)` : "",
          cLabel: it.C ? `${it.C} (${pctC}%)` : "",
        };
      })
      .sort((a, b) => b.S + b.C - (a.S + a.C));
    const safest = [...items].sort((a, b) => b.S - a.S)[0];
    const concern = [...items].sort((a, b) => b.C - a.C)[0];
    return { items, totalS, totalC, total: totalS + totalC, safest, concern, formCount: bbs.length };
  }, [bucketDetail, rows]);

  // Per-form health — count, completed, pending, top contributor per form type.
  const formHealth = useMemo(() => {
    return FORM_TYPES.map((f) => {
      const fr = rows.filter((r) => r.form_type === f.code);
      const completed = fr.filter(isCompleted).length;
      const pending = fr.length - completed;
      const completionRate = fr.length
        ? Math.round((completed / fr.length) * 100)
        : 0;
      const byPerson = countBy(fr, (r) => r.observer_name || r.emp_name);
      const top = Object.entries(byPerson).sort((a, b) => b[1] - a[1])[0];
      const byCompany = countBy(fr, (r) => r.company_name);
      const topCompany = Object.entries(byCompany).sort(
        (a, b) => b[1] - a[1],
      )[0];
      return {
        ...f,
        total: fr.length,
        completed,
        pending,
        completionRate,
        topPerson: top ? top[0] : null,
        topPersonCount: top ? top[1] : 0,
        topCompany: topCompany ? topCompany[0] : null,
        topCompanyCount: topCompany ? topCompany[1] : 0,
      };
    });
  }, [rows]);

  // Activity pulse — submissions in the last 7 days vs the prior 7.
  const activityPulse = useMemo(() => {
    const now = dayjs();
    const last7Start = now.subtract(7, "day");
    const prev7Start = now.subtract(14, "day");
    let last7 = 0;
    let prev7 = 0;
    const dayBuckets = {};
    rows.forEach((r) => {
      const d = getRowDate(r);
      if (!d) return;
      if (d.isAfter(last7Start)) last7++;
      else if (d.isAfter(prev7Start)) prev7++;
      const key = d.format("ddd");
      dayBuckets[key] = (dayBuckets[key] || 0) + 1;
    });
    const change =
      prev7 === 0
        ? last7 > 0
          ? 100
          : 0
        : Math.round(((last7 - prev7) / prev7) * 100);
    const busiest = Object.entries(dayBuckets).sort((a, b) => b[1] - a[1])[0];
    const avgPerDay = rows.length
      ? (
          rows.length / Math.max(1, Object.keys(dayBuckets).length || 1)
        ).toFixed(1)
      : "0";
    return {
      last7,
      prev7,
      change,
      busiestDay: busiest ? busiest[0] : "—",
      busiestDayCount: busiest ? busiest[1] : 0,
      avgPerDay,
    };
  }, [rows]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Safety Risk Areas */}
      <Card className="lg:col-span-3">
        <SectionTitle
          title="Safety Risk Areas"
          subtitle={`Where incidents concentrate — based on ${
            riskAreas.total
          } AIF report${riskAreas.total === 1 ? "" : "s"}`}
        />
        {riskAreas.total === 0 ? (
          <EmptyState />
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 text-left">
            <RiskList
              title="Top Project Locations"
              items={riskAreas.locations}
              accent="#DC2626"
            />
            <RiskList
              title="Top Jobs Involved"
              items={riskAreas.jobs}
              accent="#E17100"
            />
            <RiskList
              title="Top Companies"
              items={riskAreas.companies}
              accent="#155DFC"
            />
            <div className="space-y-3">
              <RiskList
                title="Common Causes"
                items={riskAreas.causes}
                accent="#9333EA"
                compact
              />
              <RiskList
                title="Body Parts Most Injured"
                items={riskAreas.parts}
                accent="#00A63E"
                compact
              />
            </div>
          </div>
        )}
      </Card>

      {/* BBS Behavior Breakdown — combined Safe vs Concern across all 4 buckets */}
      <Card className="lg:col-span-3">
        <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
          <SectionTitle
            title="BBS Safe vs Concern — All Companies"
            subtitle={`Aggregated across PPE, Body Position, Work Environment & Work Procedure · ${bbsCombined.totalForms} BBS form${bbsCombined.totalForms === 1 ? "" : "s"}`}
          />
          {bbsCombined.totalForms > 0 && (
            <button
              onClick={() => setShowBuckets((v) => !v)}
              className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-800"
            >
              {showBuckets ? "Hide" : "View More"}
            </button>
          )}
        </div>
        {bbsCombined.totalForms === 0 ? (
          <EmptyState message="No BBS submissions in this range." />
        ) : (
          <div style={{ height: 320 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={bbsCombined.data}
                layout="vertical"
                margin={{ left: 20, right: 220, top: 10, bottom: 50 }}
                barCategoryGap="25%"
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis
                  type="number"
                  allowDecimals={false}
                  fontSize={11}
                  label={{ value: "Observation Count", position: "bottom", offset: 30, fontSize: 11, fill: "#6B7280" }}
                />
                <YAxis
                  type="category"
                  dataKey="name"
                  fontSize={11}
                  width={140}
                  label={{ value: "Bucket", angle: -90, position: "insideLeft", offset: -5, fontSize: 11, fill: "#6B7280" }}
                />
                <Tooltip />
                <Legend
                  wrapperStyle={{ fontSize: 11, paddingTop: 8, right: 0 }}
                  verticalAlign="bottom"
                  align="right"
                />
                <Bar dataKey="S" name="Safe" fill="#16A34A" radius={[0, 6, 6, 0]}>
                  <LabelList
                    dataKey="S"
                    position="right"
                    content={({ x, y, width, height, value, index }) => {
                      const row = bbsCombined.data[index];
                      if (!value || !row) return null;
                      return (
                        <text
                          x={x + width + 6}
                          y={y + height / 2}
                          dy="0.35em"
                          fontSize={11}
                          fill="#166534"
                          fontWeight={600}
                        >
                          {value} ({row.safePct}%){row.safest ? ` · ${row.safest.label}` : ""}
                        </text>
                      );
                    }}
                  />
                </Bar>
                <Bar dataKey="C" name="Concern" fill="#DC2626" radius={[0, 6, 6, 0]}>
                  <LabelList
                    dataKey="C"
                    position="right"
                    content={({ x, y, width, height, value, index }) => {
                      const row = bbsCombined.data[index];
                      if (!value || !row) return null;
                      return (
                        <text
                          x={x + width + 6}
                          y={y + height / 2}
                          dy="0.35em"
                          fontSize={11}
                          fill="#991B1B"
                          fontWeight={600}
                        >
                          {value} ({row.concernPct}%){row.concern ? ` · ${row.concern.label}` : ""}
                        </text>
                      );
                    }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        {bbsCombined.totalForms > 0 && (
          <div className="mt-3">
            {showBuckets && (
              <div className="grid grid-cols-1 md:grid-cols-2 gap-2 text-left">
                {bbsCombined.data.map((b) => (
                  <button
                    key={b.name}
                    type="button"
                    onClick={() => setBucketDetail(b.name)}
                    className="rounded-lg border border-gray-100 bg-gray-50 px-3 py-2 text-xs text-left hover:bg-gray-100 hover:shadow-sm transition cursor-pointer"
                    style={{ borderLeft: `3px solid ${b.color}` }}
                  >
                    <div className="flex items-center justify-between mb-1">
                      <div className="font-semibold" style={{ color: b.color }}>
                        {b.name}
                      </div>
                      <span className="text-[10px] text-gray-400">Click for company breakdown →</span>
                    </div>
                    <div className="text-gray-700">
                      <span className="text-gray-500">Most Safe Category: </span>
                      {b.safest ? (
                        <>
                          <b className="text-green-700">{b.safest.label}</b>{" "}
                          <span className="text-green-700">({b.safest.S})</span>
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                    <div className="text-gray-700 mt-0.5">
                      <span className="text-gray-500">Most Concern Category: </span>
                      {b.concern ? (
                        <>
                          <b className="text-red-600">{b.concern.label}</b>{" "}
                          <span className="text-red-600">({b.concern.C})</span>
                        </>
                      ) : (
                        <span className="text-gray-400">—</span>
                      )}
                    </div>
                  </button>
                ))}
              </div>
            )}
          </div>
        )}

        {bucketDetail && bucketDetailStats && (
          <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4">
            <div className="bg-white rounded-xl shadow-2xl w-full max-w-4xl p-6 max-h-[90vh] overflow-y-auto">
              <div className="flex items-start justify-between mb-2 flex-wrap gap-2">
                <div className="text-left">
                  <h3 className="text-lg font-semibold text-gray-900">All Companies</h3>
                  <p className="text-xs text-gray-500">
                    {bucketDetailStats.formCount} BBS form{bucketDetailStats.formCount === 1 ? "" : "s"} · {bucketDetail} — Safe vs Concern across all companies
                  </p>
                </div>
                <button
                  onClick={() => setBucketDetail(null)}
                  className="text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600"
                >
                  Close
                </button>
              </div>

              <div className="border border-gray-200 rounded-xl p-4 bg-gray-50">
                <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                  <div className="text-left">
                    <h4 className="text-sm font-semibold text-gray-900">
                      {bucketDetail} — Safe vs Concern
                    </h4>
                    <p className="text-[11px] text-gray-500">
                      <span className="text-green-700 font-medium">{bucketDetailStats.totalS} Safe</span>
                      {" · "}
                      <span className="text-red-600 font-medium">{bucketDetailStats.totalC} Concern</span>
                      {" · "}
                      <span className="text-gray-600">{bucketDetailStats.total} obs</span>
                    </p>
                  </div>
                  <div className="flex gap-2 text-[11px] flex-wrap">
                    {bucketDetailStats.safest && bucketDetailStats.safest.S > 0 && (
                      <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full inline-flex items-center gap-1">
                        <span>Top Safest:</span>
                        <b>{bucketDetailStats.safest.label}</b>
                        <span className="px-1.5 py-0.5 bg-green-600 text-white rounded-full text-[11px] font-bold">
                          {((bucketDetailStats.safest.S / bucketDetailStats.total) * 100).toFixed(1)}%
                        </span>
                      </span>
                    )}
                    {bucketDetailStats.concern && bucketDetailStats.concern.C > 0 && (
                      <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full inline-flex items-center gap-1">
                        <span>Top Concern:</span>
                        <b>{bucketDetailStats.concern.label}</b>
                        <span className="px-1.5 py-0.5 bg-red-600 text-white rounded-full text-[11px] font-bold">
                          {((bucketDetailStats.concern.C / bucketDetailStats.total) * 100).toFixed(1)}%
                        </span>
                      </span>
                    )}
                  </div>
                </div>

                {bucketDetailStats.items.length === 0 ? (
                  <EmptyState message="No data for this bucket." />
                ) : (
                  <div style={{ height: Math.max(280, bucketDetailStats.items.length * 50) }}>
                    <ResponsiveContainer width="100%" height="100%">
                      <BarChart
                        data={bucketDetailStats.items}
                        layout="vertical"
                        margin={{ left: 10, right: 110 }}
                      >
                        <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                        <XAxis type="number" allowDecimals={false} fontSize={11} />
                        <YAxis
                          type="category"
                          dataKey="label"
                          width={180}
                          interval={0}
                          tick={<WrappedYTick />}
                        />
                        <Tooltip />
                        <Legend wrapperStyle={{ fontSize: 11 }} />
                        <Bar dataKey="S" name="Safe" fill="#16A34A" radius={[0, 4, 4, 0]}>
                          <LabelList
                            dataKey="sLabel"
                            position="right"
                            content={({ x, y, width, height, value }) =>
                              value ? (
                                <text x={x + width + 6} y={y + height / 2} dy="0.35em" fontSize={10} fill="#166534" fontWeight={600} style={{ whiteSpace: "nowrap" }}>
                                  {value}
                                </text>
                              ) : null
                            }
                          />
                        </Bar>
                        <Bar dataKey="C" name="Concern" fill="#DC2626" radius={[0, 4, 4, 0]}>
                          <LabelList
                            dataKey="cLabel"
                            position="right"
                            content={({ x, y, width, height, value }) =>
                              value ? (
                                <text x={x + width + 6} y={y + height / 2} dy="0.35em" fontSize={10} fill="#991B1B" fontWeight={600} style={{ whiteSpace: "nowrap" }}>
                                  {value}
                                </text>
                              ) : null
                            }
                          />
                        </Bar>
                      </BarChart>
                    </ResponsiveContainer>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Form mix */}
      <Card>
        <SectionTitle
          title="Form Submission Mix"
          subtitle={`${rows.length} total submissions`}
        />
        {pieData.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={pieData}
                  dataKey="value"
                  nameKey="name"
                  innerRadius={55}
                  outerRadius={90}
                  paddingAngle={3}
                >
                  {pieData.map((d) => (
                    <Cell key={d.code} fill={d.color} />
                  ))}
                </Pie>
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Completion */}
      <Card>
        <SectionTitle
          title="Completion Status"
          subtitle="Submitted vs pending sign-off"
        />
        <div className="flex items-center justify-center my-4">
          <div className="relative w-36 h-36">
            <svg viewBox="0 0 36 36" className="w-full h-full -rotate-90">
              <path
                d="M18 2.0845
                   a 15.9155 15.9155 0 0 1 0 31.831
                   a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#F1F5F9"
                strokeWidth="3.5"
              />
              <path
                d="M18 2.0845
                   a 15.9155 15.9155 0 0 1 0 31.831
                   a 15.9155 15.9155 0 0 1 0 -31.831"
                fill="none"
                stroke="#00A63E"
                strokeWidth="3.5"
                strokeDasharray={`${completionRate}, 100`}
                strokeLinecap="round"
              />
            </svg>
            <div className="absolute inset-0 flex flex-col items-center justify-center">
              <span className="text-3xl font-bold text-[#0A0A0A]">
                {completionRate}%
              </span>
              <span className="text-xs text-gray-500">Completed</span>
            </div>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-2 mt-2 text-sm">
          <div className="rounded-lg bg-green-50 border border-green-100 p-3 text-left">
            <div className="text-green-700 font-semibold text-lg">
              {completed}
            </div>
            <div className="text-xs text-green-700">Completed / Signed</div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-3 text-left">
            <div className="text-amber-700 font-semibold text-lg">
              {rows.length - completed}
            </div>
            <div className="text-xs text-amber-700">Pending</div>
          </div>
        </div>
      </Card>

      {/* Activity Pulse */}
      <Card>
        <SectionTitle
          title="Activity Pulse"
          subtitle="Submission cadence at a glance"
        />
        {rows.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-3 text-left">
            <div className="rounded-lg border border-gray-100 p-3">
              <div className="flex items-baseline justify-between">
                <span className="text-xs text-gray-500">Last 7 days</span>
                <span
                  className={`text-[11px] font-medium px-2 py-0.5 rounded-full ${
                    activityPulse.change >= 0
                      ? "bg-green-50 text-green-700"
                      : "bg-red-50 text-red-700"
                  }`}
                >
                  {activityPulse.change >= 0 ? "▲" : "▼"}{" "}
                  {Math.abs(activityPulse.change)}%
                </span>
              </div>
              <div className="text-2xl font-bold text-gray-900 mt-1">
                {activityPulse.last7}
              </div>
              <div className="text-[11px] text-gray-500">
                vs {activityPulse.prev7} the prior week
              </div>
            </div>
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded-lg bg-blue-50 border border-blue-100 p-3">
                <div className="text-xs text-blue-700">Busiest Day</div>
                <div className="text-lg font-semibold text-blue-800">
                  {activityPulse.busiestDay}
                </div>
                <div className="text-[11px] text-blue-700">
                  {activityPulse.busiestDayCount} forms
                </div>
              </div>
              <div className="rounded-lg bg-purple-50 border border-purple-100 p-3">
                <div className="text-xs text-purple-700">Avg / Active Day</div>
                <div className="text-lg font-semibold text-purple-800">
                  {activityPulse.avgPerDay}
                </div>
                <div className="text-[11px] text-purple-700">
                  across {rows.length} total
                </div>
              </div>
            </div>
          </div>
        )}
      </Card>

      {/* Top observers / employees */}
      <Card className="lg:col-span-2">
        <SectionTitle
          title="Most Active Observers / Employees"
          subtitle="People with the most submissions in this period"
        />
        {topObservers.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="space-y-2">
            {topObservers.map((p, i) => {
              const max = topObservers[0].count || 1;
              const pct = Math.round((p.count / max) * 100);
              return (
                <div key={p.name + i} className="text-left">
                  <div className="flex justify-between text-xs text-gray-700 mb-1">
                    <span className="font-medium truncate">{p.name}</span>
                    <span className="text-gray-500">{p.count}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        background: `linear-gradient(90deg, #155DFC, #4F8DFF)`,
                      }}
                    />
                  </div>
                </div>
              );
            })}
          </div>
        )}
      </Card>

      {/* Recent submissions */}
      <Card>
        <SectionTitle title="Latest Submissions" subtitle="5 most recent" />
        {rows.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-2 text-sm">
            {[...rows]
              .sort(
                (a, b) =>
                  (getRowDate(b)?.valueOf() || 0) -
                  (getRowDate(a)?.valueOf() || 0),
              )
              .slice(0, 5)
              .map((r) => (
                <li
                  key={r.id}
                  className="flex items-center justify-between gap-2 border-b border-gray-100 pb-2 last:border-b-0 last:pb-0"
                >
                  <div className="flex items-center gap-2 min-w-0">
                    <span
                      className="inline-block w-2 h-2 rounded-full shrink-0"
                      style={{ background: TYPE_COLOR[r.form_type] || "#999" }}
                    />
                    <span className="text-xs text-gray-700 truncate">
                      {TYPE_LABEL[r.form_type] || r.form_type} —{" "}
                      {r.emp_name || "—"}
                    </span>
                  </div>
                  <span className="text-[11px] text-gray-500 shrink-0">
                    {getRowDate(r)?.format("MMM D") || ""}
                  </span>
                </li>
              ))}
          </ul>
        )}
      </Card>

      {/* Per-Form Health */}
      <Card>
        <SectionTitle
          title="Per-Form Health"
          subtitle="Status across all 4 forms"
        />
        <div className="space-y-3 text-left">
          {formHealth.map((f) => (
            <div key={f.code} className="border border-gray-100 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div className="flex items-center gap-2 min-w-0">
                  <span
                    className="inline-block w-2.5 h-2.5 rounded-full shrink-0"
                    style={{ background: TYPE_COLOR[f.code] || "#999" }}
                  />
                  <span className="text-sm font-medium text-gray-900 truncate">
                    {f.label}
                  </span>
                </div>
                <span className="text-xs text-gray-500 shrink-0">
                  {f.total} total
                </span>
              </div>
              <div className="flex justify-between text-[11px] text-gray-500 mb-1">
                <span>{f.completed} completed</span>
                <span>{f.pending} pending</span>
                <span className="font-medium text-gray-700">
                  {f.completionRate}%
                </span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden mb-2">
                <div
                  className="h-full rounded-full"
                  style={{
                    width: `${f.completionRate}%`,
                    background: TYPE_COLOR[f.code] || "#155DFC",
                  }}
                />
              </div>
              <div className="text-[11px] text-gray-600 space-y-0.5">
                {f.topPerson && (
                  <div className="truncate">
                    <span className="text-gray-400">Top submitter: </span>
                    {f.topPerson}{" "}
                    <span className="text-gray-400">({f.topPersonCount})</span>
                  </div>
                )}
                {f.topCompany && (
                  <div className="truncate">
                    <span className="text-gray-400">Top company: </span>
                    {f.topCompany}{" "}
                    <span className="text-gray-400">({f.topCompanyCount})</span>
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
};

const RiskList = ({ title, items, accent, compact }) => {
  if (!items || items.length === 0) {
    return (
      <div>
        <div className="text-xs font-semibold text-gray-700 mb-2">{title}</div>
        <div className="text-xs text-gray-400">No data</div>
      </div>
    );
  }
  const max = items[0]?.value || 1;
  return (
    <div>
      <div className="text-xs font-semibold text-gray-700 mb-2">{title}</div>
      <div className={compact ? "space-y-1.5" : "space-y-2"}>
        {items.map((it, i) => {
          const pct = Math.round((it.value / max) * 100);
          return (
            <div key={it.name + i}>
              <div className="flex justify-between text-[11px] text-gray-700 mb-0.5">
                <span className="truncate pr-2">{it.name}</span>
                <span className="text-gray-500 shrink-0">{it.value}</span>
              </div>
              <div className="w-full h-1.5 bg-gray-100 rounded-full overflow-hidden">
                <div
                  className="h-full rounded-full"
                  style={{ width: `${pct}%`, background: accent }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

const Incidents = ({ rows }) => {
  const aif = rows.filter((r) => r.form_type === "AIF");
  const completedAif = aif.filter(isCompleted).length;
  const monthly = useMemo(() => buildMonthlyTrend(aif, 6), [aif]);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRow, setViewRow] = useState(null);

  const handleViewForm = async (rowid) => {
    try {
      setViewLoading(true);
      const resp = await api.get("/v1/forms/get_by_id", { params: { rowid } });
      if (resp.status === 200 && resp.data?.[0]) {
        setViewRow(resp.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch incident", e);
    } finally {
      setViewLoading(false);
    }
  };

  // Aggregate AIF incident params from backend `incident_meta`.
  const incidentParams = useMemo(() => {
    const causes = {};
    const bodyParts = {};
    const injuryTypes = {};
    const classifications = {};
    let clientNotifiedYes = 0;
    let clientNotifiedNo = 0;
    aif.forEach((r) => {
      const m = r.incident_meta || {};
      if (m.incident_cause)
        causes[m.incident_cause] = (causes[m.incident_cause] || 0) + 1;
      if (m.injury_type)
        injuryTypes[m.injury_type] = (injuryTypes[m.injury_type] || 0) + 1;
      (m.body_parts || []).forEach((p) => {
        bodyParts[p] = (bodyParts[p] || 0) + 1;
      });
      (m.classifications || []).forEach((c) => {
        classifications[c] = (classifications[c] || 0) + 1;
      });
      if ((m.client_notified || "").toLowerCase() === "yes")
        clientNotifiedYes += 1;
      else if ((m.client_notified || "").toLowerCase() === "no")
        clientNotifiedNo += 1;
    });
    const toSorted = (m) =>
      Object.entries(m)
        .sort((a, b) => b[1] - a[1])
        .map(([name, value]) => ({ name, value }));
    return {
      causes: toSorted(causes).slice(0, 8),
      bodyParts: toSorted(bodyParts).slice(0, 10),
      injuryTypes: toSorted(injuryTypes).slice(0, 8),
      classifications: toSorted(classifications),
      clientNotifiedYes,
      clientNotifiedNo,
    };
  }, [aif]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-3">
        <Card>
          <SectionTitle
            title="Incidents Reported (Monthly)"
            subtitle="Accident / Incident submissions over the last 6 months"
          />
          {aif.length === 0 ? (
            <EmptyState message="No accident/incident reports in this range." />
          ) : (
            <div className="h-[300px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart data={monthly}>
                  <defs>
                    <linearGradient id="aifGrad" x1="0" y1="0" x2="0" y2="1">
                      <stop
                        offset="0%"
                        stopColor="#E7000B"
                        stopOpacity={0.45}
                      />
                      <stop
                        offset="100%"
                        stopColor="#E7000B"
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="AIF"
                    stroke="#E7000B"
                    strokeWidth={2}
                    fill="url(#aifGrad)"
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Top Incident Causes */}
      <Card className="lg:col-span-2">
        <SectionTitle
          title="Top Incident Causes"
          subtitle="Most frequent root causes reported on AIF forms"
        />
        {incidentParams.causes.length === 0 ? (
          <EmptyState message="No cause data captured." />
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={incidentParams.causes}
                layout="vertical"
                margin={{ left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="name"
                  fontSize={11}
                  width={160}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#E7000B" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Classifications */}
      <Card>
        <SectionTitle
          title="Incident Classification"
          subtitle="Reported damage / event types"
        />
        {incidentParams.classifications.length === 0 ? (
          <EmptyState message="No classifications captured." />
        ) : (
          <ul className="space-y-2 text-sm max-h-[260px] overflow-y-auto pr-1">
            {incidentParams.classifications.map((c, i) => {
              const max = incidentParams.classifications[0].value || 1;
              const pct = Math.round((c.value / max) * 100);
              return (
                <li key={c.name + i} className="text-left">
                  <div className="flex justify-between text-xs text-gray-700 mb-1">
                    <span className="font-medium truncate">{c.name}</span>
                    <span className="text-gray-500">{c.value}</span>
                  </div>
                  <div className="w-full h-2 bg-gray-100 rounded-full overflow-hidden">
                    <div
                      className="h-full rounded-full"
                      style={{
                        width: `${pct}%`,
                        background: "linear-gradient(90deg, #E7000B, #FB7185)",
                      }}
                    />
                  </div>
                </li>
              );
            })}
          </ul>
        )}
      </Card>

      {/* Body Parts */}
      <Card className="lg:col-span-2">
        <SectionTitle
          title="Body Parts Injured"
          subtitle="Most affected body parts across reports"
        />
        {incidentParams.bodyParts.length === 0 ? (
          <EmptyState message="No injury location data captured." />
        ) : (
          <div className="h-[260px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={incidentParams.bodyParts}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  fontSize={11}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                />
                <YAxis allowDecimals={false} fontSize={11} />
                <Tooltip />
                <Bar dataKey="value" fill="#E17100" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {/* Injury Types + Risk summary */}
      <Card>
        <SectionTitle
          title="Injury Types"
          subtitle="Recorded injury categories"
        />
        {incidentParams.injuryTypes.length === 0 ? (
          <EmptyState message="No injury types captured." />
        ) : (
          <ul className="space-y-2 text-sm max-h-[200px] overflow-y-auto pr-1">
            {incidentParams.injuryTypes.map((t, i) => (
              <li
                key={t.name + i}
                className="flex items-center justify-between border-b border-gray-100 pb-1.5 last:border-b-0 last:pb-0 text-left"
              >
                <span className="text-gray-800 truncate">{t.name}</span>
                <span className="text-xs font-semibold text-[#E7000B] bg-red-50 rounded-full px-2 py-0.5">
                  {t.value}
                </span>
              </li>
            ))}
          </ul>
        )}
        <div className="grid grid-cols-2 gap-2 mt-4 text-sm">
          <div className="rounded-lg bg-green-50 border border-green-100 p-2 text-left">
            <div className="text-green-700 font-semibold text-base">
              {incidentParams.clientNotifiedYes}
            </div>
            <div className="text-[11px] text-green-700">Client notified</div>
          </div>
          <div className="rounded-lg bg-amber-50 border border-amber-100 p-2 text-left">
            <div className="text-amber-700 font-semibold text-base">
              {incidentParams.clientNotifiedNo}
            </div>
            <div className="text-[11px] text-amber-700">Not notified</div>
          </div>
        </div>
      </Card>

      {/* Recent incidents list */}
      <Card className="lg:col-span-3">
        <SectionTitle
          title="Recent Incident Reports"
          subtitle={`${aif.length} total · ${completedAif} completed`}
        />
        {aif.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="text-[11px] uppercase tracking-wider text-gray-500 border-b">
                <tr>
                  <th className="text-left px-2 py-2">Date</th>
                  <th className="text-left px-2 py-2">Employee</th>
                  <th className="text-left px-2 py-2">Job</th>
                  <th className="text-left px-2 py-2">Method</th>
                  <th className="text-left px-2 py-2">Status</th>
                  <th className="text-center px-2 py-2">Action</th>
                </tr>
              </thead>
              <tbody>
                {[...aif]
                  .sort(
                    (a, b) =>
                      (getRowDate(b)?.valueOf() || 0) -
                      (getRowDate(a)?.valueOf() || 0),
                  )
                  .slice(0, 8)
                  .map((r) => (
                    <tr key={r.id} className="border-b border-gray-100">
                      <td className="px-2 py-2 text-gray-700">
                        {getRowDate(r)?.format("MMM D, YYYY") || "—"}
                      </td>
                      <td className="px-2 py-2 text-gray-800">
                        {r.emp_name || "—"}
                      </td>
                      <td className="px-2 py-2 text-gray-600">
                        {r.jobDescription || "—"}
                      </td>
                      <td className="px-2 py-2 text-gray-600">
                        {r.method || "—"}
                      </td>
                      <td className="px-2 py-2">
                        {isCompleted(r) ? (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-green-100 text-green-700">
                            Completed
                          </span>
                        ) : (
                          <span className="inline-block px-2 py-0.5 rounded-full text-[11px] bg-amber-100 text-amber-700">
                            Pending
                          </span>
                        )}
                      </td>
                      <td className="px-2 py-2 text-center">
                        <button
                          onClick={() => handleViewForm(r.id)}
                          className="bg-[#1E4976] text-white p-1.5 rounded hover:bg-blue-700 inline-flex items-center justify-center"
                          title="View incident report"
                        >
                          <RemoveRedEyeIcon style={{ width: 18, height: 18 }} />
                        </button>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      <Backdrop open={viewLoading} sx={{ zIndex: 9998 }}>
        <CircularProgress />
      </Backdrop>
      {viewRow && (
        <AIFFormModal
          formType="AIF"
          rowData={viewRow}
          formView={!!viewRow}
          setFormView={() => setViewRow(null)}
        />
      )}
    </div>
  );
};

const wrapLabel = (text, maxChars = 18) => {
  if (!text) return [""];
  if (text.length <= maxChars) return [text];
  const words = String(text).split(" ");
  const lines = [];
  let cur = "";
  words.forEach((w) => {
    if ((cur + " " + w).trim().length > maxChars && cur) {
      lines.push(cur.trim());
      cur = w;
    } else {
      cur = (cur + " " + w).trim();
    }
    if (lines.length === 1 && cur.length > maxChars) {
      lines.push(cur.slice(0, maxChars - 1) + "…");
      cur = "";
    }
  });
  if (cur && lines.length < 2) lines.push(cur);
  return lines.slice(0, 2);
};

const WrappedYTick = ({ x, y, payload }) => {
  const lines = wrapLabel(payload.value, 24);
  return (
    <g transform={`translate(${x},${y})`}>
      {lines.map((ln, i) => (
        <text
          key={i}
          x={-4}
          y={(i - (lines.length - 1) / 2) * 12}
          textAnchor="end"
          fontSize={11}
          fill="#374151"
        >
          {ln}
        </text>
      ))}
    </g>
  );
};

const BbsData = ({ rows }) => {
  const bbs = rows.filter((r) => r.form_type === "BBS");

  // Submissions per company (skip rows without a recognized company)
  const perCompany = useMemo(() => {
    const map = {};
    bbs.forEach((r) => {
      const name = (r.company_name || "").trim();
      if (!name) return;
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [bbs]);

  // Submissions per method
  const perMethod = useMemo(() => {
    const m = countBy(bbs, (r) => r.method || "Manual");
    return Object.entries(m).map(([k, v]) => ({ name: k, value: v }));
  }, [bbs]);

  // Trend over months
  const monthly = useMemo(() => buildMonthlyTrend(bbs, 6), [bbs]);

  // Top observers
  const observers = useMemo(() => {
    const m = countBy(bbs, (r) => r.observer_name || r.emp_name);
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 5);
  }, [bbs]);

  // Rows without a real job_id (0/null) and without a description are skipped.
  const topJobs = useMemo(() => {
    const map = {};
    bbs.forEach((r) => {
      const name = (r.jobDescription || "").trim();
      const id = r.job_id && Number(r.job_id) > 0 ? String(r.job_id) : null;
      if (!id && !name) return;
      const key = `${id || "—"}__${name}`;
      const label = id && name ? `${id} - ${name}` : id ? `(${id})` : name;
      if (!map[key]) {
        map[key] = { name: label, value: 0, jobId: id, jobName: name, key };
      }
      map[key].value += 1;
    });
    return Object.values(map)
      .sort((a, b) => b.value - a.value)
      .slice(0, 10);
  }, [bbs]);

  // Click-to-popup: list BBS forms under the selected job.
  const [selectedJob, setSelectedJob] = useState(null);
  const [viewLoading, setViewLoading] = useState(false);
  const [viewRow, setViewRow] = useState(null);
  const [jobQuery, setJobQuery] = useState("");
  const [dropdownOpen, setDropdownOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState("");
  const [allCompanies, setAllCompanies] = useState([]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/v1/timesheets/company-details");
        if (!cancelled && resp.status === 200 && Array.isArray(resp.data)) {
          setAllCompanies(resp.data);
        }
      } catch (e) {
        console.error("Failed to load companies", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  const selectedCompanyName = useMemo(() => {
    if (selectedCompany === "ALL") return "All Companies";
    const c = allCompanies.find(
      (x) => String(x.id) === String(selectedCompany),
    );
    return c ? c.name : "";
  }, [allCompanies, selectedCompany]);

  const formsForCompany = useMemo(() => {
    if (!selectedCompany) return [];
    if (selectedCompany === "ALL") return bbs;
    return bbs.filter((r) => String(r.companyid) === String(selectedCompany));
  }, [bbs, selectedCompany]);

  const LABEL_ALIASES = {
    // PPE
    "ojos y cara": "Eye & Face",
    mano: "Hand",
    cuerpo: "Body",
    cabeza: "Head",
    respiratorio: "Respiratory",
    audición: "Hearing",
    audicion: "Hearing",
    // Body Position
    "posicionamiento corporal adecuado (línea de fuego)":
      "Proper Body Positioning (Line of fire)",
    "posicionamiento corporal adecuado (linea de fuego)":
      "Proper Body Positioning (Line of fire)",
    "mecánica de levantamiento manual": "Manual Lifting Mechanics",
    "mecanica de levantamiento manual": "Manual Lifting Mechanics",
    "manual lifting mechanics": "Manual Lifting Mechanics",
    repetición: "Repetition",
    repeticion: "Repetition",
    "puntos de pellizco": "Pinch Points",
    "tirar/empujar": "Pulling/Pushing",
    extender: "Extending",
    extendiendo: "Extending",
    "posicionamiento adecuado del cuerpo (línea de fuego)":
      "Proper Body Positioning (Line of fire)",
    "posicionamiento adecuado del cuerpo (linea de fuego)":
      "Proper Body Positioning (Line of fire)",
    "levantamiento manual": "Manual Lifting Mechanics",
    "tirando/empujando": "Pulling/Pushing",
    // Work Environment
    "resbalones, tropiezos, caídas": "Slips, Trips, Falls",
    "resbalones, tropiezos, caidas": "Slips, Trips, Falls",
    "resbalones, tropezones, caídas": "Slips, Trips, Falls",
    "resbalones, tropezones, caidas": "Slips, Trips, Falls",
    "área de trabajo congestionada": "Congested Work Area",
    "area de trabajo congestionada": "Congested Work Area",
    "barricadas/señalización": "Barricades/Signage",
    "barricadas/senalizacion": "Barricades/Signage",
    excavaciones: "Excavations",
    "protección contra caídas": "Fall Protection",
    "proteccion contra caidas": "Fall Protection",
    "control de derrames": "Spill Control",
    // Work Procedure
    "tarea cubierta por jsa": "JSA Cover Task",
    "reconocimiento de peligros": "Hazard Recognition",
    "aislamiento de energía (loto)": "Energy Isolation (LOTO)",
    "aislamiento de energia (loto)": "Energy Isolation (LOTO)",
    "operaciones simultáneas (simops)": "Simultaneous Operations (SIMOPS)",
    "operaciones simultaneas (simops)": "Simultaneous Operations (SIMOPS)",
    "herramientas manuales/eléctricas": "Hand/Power Tools",
    "herramientas manuales/electricas": "Hand/Power Tools",
    "equipo móvil": "Mobile Equipment",
    "equipo movil": "Mobile Equipment",
  };
  const canonicalLabel = (raw) => {
    if (!raw) return raw;
    const key = String(raw).trim().toLowerCase();
    return LABEL_ALIASES[key] || raw;
  };

  const buildCompanyBucketStats = (bucketKey) => {
    const perItem = {};
    let totalS = 0;
    let totalC = 0;
    formsForCompany.forEach((r) => {
      const bucket = r.bbs_meta?.[bucketKey];
      if (!Array.isArray(bucket)) return;
      bucket.forEach(({ label, answer }) => {
        if (!label) return;
        const canon = canonicalLabel(label);
        if (!perItem[canon]) perItem[canon] = { label: canon, S: 0, C: 0 };
        if (answer === "S") {
          perItem[canon].S += 1;
          totalS += 1;
        } else if (answer === "C") {
          perItem[canon].C += 1;
          totalC += 1;
        }
      });
    });
    const total = totalS + totalC || 1;
    const items = Object.values(perItem)
      .map((it) => {
        const pctS = +((it.S / total) * 100).toFixed(1);
        const pctC = +((it.C / total) * 100).toFixed(1);
        return {
          ...it,
          pctS,
          pctC,
          sLabel: it.S ? `${it.S} (${pctS}%)` : "",
          cLabel: it.C ? `${it.C} (${pctC}%)` : "",
        };
      })
      .sort((a, b) => b.S + b.C - (a.S + a.C));
    const safest = [...items].sort((a, b) => b.S - a.S)[0];
    const concern = [...items].sort((a, b) => b.C - a.C)[0];
    return { items, totalS, totalC, total: totalS + totalC, safest, concern };
  };

  const companyPpe = useMemo(
    () => buildCompanyBucketStats("PPE"),
    [formsForCompany],
  );
  const companyBody = useMemo(
    () => buildCompanyBucketStats("Body Position"),
    [formsForCompany],
  );
  const companyEnv = useMemo(
    () => buildCompanyBucketStats("Work Environment"),
    [formsForCompany],
  );
  const companyProc = useMemo(
    () => buildCompanyBucketStats("Work Procedure"),
    [formsForCompany],
  );

  // Full job list (across all BBS rows) for the search dropdown.
  const allJobs = useMemo(() => {
    const map = {};
    bbs.forEach((r) => {
      const name = (r.jobDescription || "").trim();
      const id = r.job_id && Number(r.job_id) > 0 ? String(r.job_id) : null;
      if (!id && !name) return;
      const key = `${id || "—"}__${name}`;
      const label = id && name ? `(${id} - ${name})` : id ? `(${id})` : name;
      if (!map[key]) {
        map[key] = { name: label, value: 0, jobId: id, jobName: name, key };
      }
      map[key].value += 1;
    });
    return Object.values(map).sort((a, b) => b.value - a.value);
  }, [bbs]);

  const jobSearchMatches = useMemo(() => {
    const q = jobQuery.trim().toLowerCase();
    if (!q) return [];
    return allJobs.filter((j) => j.name.toLowerCase().includes(q)).slice(0, 8);
  }, [allJobs, jobQuery]);

  // While the user is typing, preview the best match inline (no click needed).
  const previewJob = jobQuery.trim() ? jobSearchMatches[0] : null;

  // Popup (bar/dropdown click) wins; otherwise preview the search match inline.
  const activeJob = selectedJob || previewJob;

  const formsForJob = useMemo(() => {
    if (!activeJob) return [];
    return bbs.filter((r) => {
      const name = (r.jobDescription || "").trim();
      const id = r.job_id && Number(r.job_id) > 0 ? String(r.job_id) : null;
      return id === activeJob.jobId && name === activeJob.jobName;
    });
  }, [bbs, activeJob]);

  // Aggregate S/C answers across forms shown in the popup, for a given bucket.
  const buildBucketStats = (bucketKey) => {
    const perItem = {};
    let totalS = 0;
    let totalC = 0;
    formsForJob.forEach((r) => {
      const bucket = r.bbs_meta?.[bucketKey];
      if (!Array.isArray(bucket)) return;
      bucket.forEach(({ label, answer }) => {
        if (!label) return;
        const canon = canonicalLabel(label);
        if (!perItem[canon]) perItem[canon] = { label: canon, S: 0, C: 0 };
        if (answer === "S") {
          perItem[canon].S += 1;
          totalS += 1;
        } else if (answer === "C") {
          perItem[canon].C += 1;
          totalC += 1;
        }
      });
    });
    const items = Object.values(perItem).sort(
      (a, b) => b.S + b.C - (a.S + a.C),
    );
    const safest = [...items].sort((a, b) => b.S - a.S)[0];
    const concern = [...items].sort((a, b) => b.C - a.C)[0];
    return { items, totalS, totalC, safest, concern };
  };

  const ppeStats = useMemo(() => buildBucketStats("PPE"), [formsForJob]);
  const bodyStats = useMemo(
    () => buildBucketStats("Body Position"),
    [formsForJob],
  );
  const envStats = useMemo(
    () => buildBucketStats("Work Environment"),
    [formsForJob],
  );
  const procStats = useMemo(
    () => buildBucketStats("Work Procedure"),
    [formsForJob],
  );

  const handleViewBbsForm = async (rowid) => {
    try {
      setViewLoading(true);
      const resp = await api.get("/v1/forms/get_by_id", { params: { rowid } });
      if (resp.status === 200 && resp.data?.[0]) {
        setViewRow(resp.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch BBS form", e);
    } finally {
      setViewLoading(false);
    }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <div className="lg:col-span-3 flex justify-between items-center gap-2 flex-wrap">
        <div className="flex items-center gap-2">
          <select
            value={selectedCompany}
            onChange={(e) => setSelectedCompany(e.target.value)}
            className="px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white shadow-sm outline-none focus:border-gray-400"
          >
            <option value="">Select company…</option>
            <option value="ALL">All Companies</option>
            {allCompanies.map((c) => (
              <option key={c.id} value={c.id}>
                {c.name}
              </option>
            ))}
          </select>
          {selectedCompany && (
            <button
              onClick={() => setSelectedCompany("")}
              className="text-xs px-2 py-1.5 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600"
            >
              Clear
            </button>
          )}
        </div>
        <div className="relative w-72">
          <input
            type="text"
            value={jobQuery}
            onChange={(e) => {
              setJobQuery(e.target.value);
              setDropdownOpen(true);
            }}
            placeholder="Search job by id or name…"
            className="w-full px-3 py-1.5 text-xs border border-gray-200 rounded-lg bg-white shadow-sm outline-none focus:border-gray-400"
          />
          {dropdownOpen && jobQuery && jobSearchMatches.length > 0 && (
            <ul className="absolute right-0 mt-1 w-full max-h-72 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-50">
              {jobSearchMatches.map((j) => (
                <li key={j.key}>
                  <button
                    type="button"
                    onClick={() => {
                      setJobQuery(j.name);
                      setDropdownOpen(false);
                    }}
                    className="w-full text-left px-3 py-2 text-xs hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                  >
                    <div className="font-medium text-gray-800 truncate">
                      {j.name}
                    </div>
                    <div className="text-[11px] text-gray-500">
                      {j.value} form{j.value === 1 ? "" : "s"}
                    </div>
                  </button>
                </li>
              ))}
            </ul>
          )}
          {dropdownOpen && jobQuery && jobSearchMatches.length === 0 && (
            <div className="absolute right-0 mt-1 w-full bg-white border border-gray-200 rounded-lg shadow-lg z-50 px-3 py-2 text-xs text-gray-500">
              No jobs match.
            </div>
          )}
        </div>
      </div>

      {/* Inline charts aggregated for the selected company */}
      {selectedCompany && (
        <Card className="lg:col-span-3">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div className="text-left">
              <h3 className="text-base font-semibold text-gray-900">
                {selectedCompanyName}
              </h3>
              <p className="text-xs text-gray-500">
                {formsForCompany.length} BBS form
                {formsForCompany.length === 1 ? "" : "s"} · Safe vs Concern
                counts across PPE, Body Position, Work Environment & Work
                Procedure
              </p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { key: "PPE", title: "PPE — Safe vs Concern", stats: companyPpe },
              {
                key: "Body Position",
                title: "Body Position — Safe vs Concern",
                stats: companyBody,
              },
              {
                key: "Work Environment",
                title: "Work Environment — Safe vs Concern",
                stats: companyEnv,
              },
              {
                key: "Work Procedure",
                title: "Work Procedure — Safe vs Concern",
                stats: companyProc,
              },
            ].map(
              ({ key, title, stats }) =>
                stats.items.length > 0 && (
                  <div
                    key={key}
                    className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                  >
                    <div className="flex flex-col items-start justify-between mb-3 flex-wrap gap-2">
                      <div className="text-left">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {title}
                        </h4>
                        <p className="text-[11px] text-gray-500">
                          <span className="text-green-700 font-medium">
                            {stats.totalS} Safe
                          </span>
                          {" · "}
                          <span className="text-red-600 font-medium">
                            {stats.totalC} Concern
                          </span>
                          {" · "}
                          <span className="text-gray-600">
                            {stats.total} obs
                          </span>
                        </p>
                      </div>
                      <div className="flex flex-col gap-2 text-[11px]">
                        {stats.safest && stats.safest.S > 0 && (
                          <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full inline-flex items-center gap-1">
                            <span>Top Safest:</span>
                            <b>{stats.safest.label}</b>
                            <span className="px-1.5 py-0.5 bg-green-600 text-white rounded-full text-[11px] font-bold">
                              {((stats.safest.S / stats.total) * 100).toFixed(
                                1,
                              )}
                              %
                            </span>
                          </span>
                        )}
                        {stats.concern && stats.concern.C > 0 && (
                          <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full inline-flex items-center gap-1">
                            <span>Top Concern:</span>
                            <b>{stats.concern.label}</b>
                            <span className="px-1.5 py-0.5 bg-red-600 text-white rounded-full text-[11px] font-bold">
                              {((stats.concern.C / stats.total) * 100).toFixed(
                                1,
                              )}
                              %
                            </span>
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      style={{ height: Math.max(220, stats.items.length * 28) }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={stats.items}
                          layout="vertical"
                          margin={{ left: 10, right: 110 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            horizontal={false}
                          />
                          <XAxis
                            type="number"
                            allowDecimals={false}
                            fontSize={11}
                          />
                          <YAxis
                            type="category"
                            dataKey="label"
                            width={180}
                            interval={0}
                            tick={<WrappedYTick />}
                          />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar
                            dataKey="S"
                            name="Safe"
                            fill="#16A34A"
                            radius={[0, 4, 4, 0]}
                          >
                            <LabelList
                              dataKey="sLabel"
                              position="right"
                              content={({ x, y, width, height, value }) =>
                                value ? (
                                  <text
                                    x={x + width + 6}
                                    y={y + height / 2}
                                    dy="0.35em"
                                    fontSize={10}
                                    fill="#166534"
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    {value}
                                  </text>
                                ) : null
                              }
                            />
                          </Bar>
                          <Bar
                            dataKey="C"
                            name="Concern"
                            fill="#DC2626"
                            radius={[0, 4, 4, 0]}
                          >
                            <LabelList
                              dataKey="cLabel"
                              position="right"
                              content={({ x, y, width, height, value }) =>
                                value ? (
                                  <text
                                    x={x + width + 6}
                                    y={y + height / 2}
                                    dy="0.35em"
                                    fontSize={10}
                                    fill="#991B1B"
                                    fontWeight={600}
                                    style={{ whiteSpace: "nowrap" }}
                                  >
                                    {value}
                                  </text>
                                ) : null
                              }
                            />
                          </Bar>
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ),
            )}
          </div>
        </Card>
      )}

      {/* Inline charts for the search-selected job */}
      {previewJob && !selectedJob && (
        <Card className="lg:col-span-3">
          <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
            <div className="text-left">
              <h3 className="text-base font-semibold text-gray-900">
                {previewJob.name}
              </h3>
              <p className="text-xs text-gray-500">
                {formsForJob.length} BBS form
                {formsForJob.length === 1 ? "" : "s"} · Aggregated S/C across
                PPE, Body Position, Work Environment & Work Procedure
              </p>
            </div>
            <div className="flex items-center gap-2">
              <button
                onClick={() => setSelectedJob(previewJob)}
                className="text-xs px-3 py-1.5 rounded-md bg-gray-900 text-white hover:bg-gray-800"
              >
                View More
              </button>
              <button
                onClick={() => {
                  setJobQuery("");
                  setDropdownOpen(false);
                }}
                className="text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600"
              >
                Clear
              </button>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            {[
              { key: "PPE", title: "PPE — Safe vs Concern", stats: ppeStats },
              {
                key: "Body Position",
                title: "Body Position — Safe vs Concern",
                stats: bodyStats,
              },
              {
                key: "Work Environment",
                title: "Work Environment — Safe vs Concern",
                stats: envStats,
              },
              {
                key: "Work Procedure",
                title: "Work Procedure — Safe vs Concern",
                stats: procStats,
              },
            ].map(
              ({ key, title, stats }) =>
                stats.items.length > 0 && (
                  <div
                    key={key}
                    className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                  >
                    <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                      <div className="text-left">
                        <h4 className="text-sm font-semibold text-gray-900">
                          {title}
                        </h4>
                        <p className="text-[11px] text-gray-500">
                          <span className="text-green-700 font-medium">
                            {stats.totalS} Safe
                          </span>
                          {" · "}
                          <span className="text-red-600 font-medium">
                            {stats.totalC} Concern
                          </span>
                        </p>
                      </div>
                      <div className="flex gap-2 text-[11px] flex-wrap">
                        {stats.safest && stats.safest.S > 0 && (
                          <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">
                            Top Safest: <b>{stats.safest.label}</b> (
                            {stats.safest.S})
                          </span>
                        )}
                        {stats.concern && stats.concern.C > 0 && (
                          <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full">
                            Top Concern: <b>{stats.concern.label}</b> (
                            {stats.concern.C})
                          </span>
                        )}
                      </div>
                    </div>
                    <div
                      style={{ height: Math.max(220, stats.items.length * 28) }}
                    >
                      <ResponsiveContainer width="100%" height="100%">
                        <BarChart
                          data={stats.items}
                          layout="vertical"
                          margin={{ left: 10, right: 20 }}
                        >
                          <CartesianGrid
                            strokeDasharray="3 3"
                            horizontal={false}
                          />
                          <XAxis
                            type="number"
                            allowDecimals={false}
                            fontSize={11}
                          />
                          <YAxis
                            type="category"
                            dataKey="label"
                            fontSize={11}
                            width={140}
                            tickFormatter={(v) =>
                              v && v.length > 22 ? `${v.slice(0, 20)}…` : v
                            }
                          />
                          <Tooltip />
                          <Legend wrapperStyle={{ fontSize: 11 }} />
                          <Bar
                            dataKey="S"
                            name="Safe"
                            fill="#16A34A"
                            radius={[0, 4, 4, 0]}
                          />
                          <Bar
                            dataKey="C"
                            name="Concern"
                            fill="#DC2626"
                            radius={[0, 4, 4, 0]}
                          />
                        </BarChart>
                      </ResponsiveContainer>
                    </div>
                  </div>
                ),
            )}
          </div>
        </Card>
      )}

      <Card className="lg:col-span-3">
        <SectionTitle
          title="Top 10 Jobs"
          subtitle="Most observed jobs across BBS submissions"
        />
        {topJobs.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ height: Math.max(300, topJobs.length * 36) }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topJobs}
                layout="vertical"
                margin={{ left: 20, right: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="name"
                  fontSize={11}
                  width={220}
                  tickFormatter={(v) =>
                    v && v.length > 32 ? `${v.slice(0, 30)}…` : v
                  }
                />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill="#155DFC"
                  radius={[0, 6, 6, 0]}
                  cursor="pointer"
                  onClick={(d) => {
                    const row = d?.payload || d;
                    if (row && (row.jobId || row.jobName)) setSelectedJob(row);
                  }}
                />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
        <p className="text-xs text-gray-400 mt-2 text-left">
          Click a bar to see the forms filed under that job.
        </p>
      </Card>

      <Card className="lg:col-span-2">
        <SectionTitle
          title="BBS Submissions per Month"
          subtitle="Behaviour-Based Safety cards over the last 6 months"
        />
        {bbs.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={monthly}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} />
                <Tooltip />
                <Bar dataKey="BBS" fill="#155DFC" radius={[6, 6, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle
          title="Submission Method"
          subtitle="How BBS cards are filed"
        />
        {perMethod.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <PieChart>
                <Pie
                  data={perMethod}
                  dataKey="value"
                  nameKey="name"
                  outerRadius={90}
                  label={(d) => d.name}
                  fontSize={11}
                >
                  {perMethod.map((_, i) => (
                    <Cell
                      key={i}
                      fill={["#155DFC", "#00A63E", "#E17100"][i % 3]}
                    />
                  ))}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card className="lg:col-span-2">
        <SectionTitle
          title="By Company"
          subtitle="Top organizations by BBS volume"
        />
        {perCompany.length === 0 ? (
          <EmptyState />
        ) : (
          <div className="h-[280px]">
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={perCompany}
                layout="vertical"
                margin={{ left: 20 }}
              >
                <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                <XAxis type="number" allowDecimals={false} fontSize={11} />
                <YAxis
                  type="category"
                  dataKey="name"
                  fontSize={11}
                  width={130}
                />
                <Tooltip />
                <Bar dataKey="value" fill="#00A63E" radius={[0, 6, 6, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle title="Top Observers" subtitle="Most active filers" />
        {observers.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-2 text-sm">
            {observers.map(([name, count], i) => (
              <li
                key={name + i}
                className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0 last:pb-0 text-left"
              >
                <span className="text-gray-800 truncate">{name}</span>
                <span className="text-xs font-semibold text-[#155DFC] bg-blue-50 rounded-full px-2 py-0.5">
                  {count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Click-through: forms filed under the selected job */}
      {selectedJob && !viewRow && (
        <div
          className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] px-4"
          onClick={() => setSelectedJob(null)}
        >
          <div
            className="bg-white rounded-xl shadow-lg w-full max-w-7xl max-h-[92vh] flex flex-col"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-start justify-between p-5 border-b">
              <div className="text-left">
                <h3 className="text-base font-semibold text-gray-900">
                  BBS Forms — {selectedJob.name}
                </h3>
                <p className="text-xs text-gray-500 mt-0.5">
                  {formsForJob.length} form{formsForJob.length === 1 ? "" : "s"}{" "}
                  filed under this job in the selected range.
                </p>
              </div>
              <button
                onClick={() => setSelectedJob(null)}
                className="text-gray-400 hover:text-gray-700 text-xl leading-none"
                aria-label="Close"
              >
                ×
              </button>
            </div>
            <div className="overflow-y-auto p-4 space-y-3">
              {/* PPE chart */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                {[
                  {
                    key: "PPE",
                    title: "PPE — Safe vs Concern",
                    stats: ppeStats,
                  },
                  {
                    key: "Body Position",
                    title: "Body Position — Safe vs Concern",
                    stats: bodyStats,
                  },
                  {
                    key: "Work Environment",
                    title: "Work Environment — Safe vs Concern",
                    stats: envStats,
                  },
                  {
                    key: "Work Procedure",
                    title: "Work Procedure — Safe vs Concern",
                    stats: procStats,
                  },
                ].map(
                  ({ key, title, stats }) =>
                    formsForJob.length > 0 &&
                    stats.items.length > 0 && (
                      <div
                        key={key}
                        className="border border-gray-200 rounded-xl p-4 bg-gray-50"
                      >
                        <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
                          <div className="text-left">
                            <h4 className="text-sm font-semibold text-gray-900">
                              {title}
                            </h4>
                            <p className="text-[11px] text-gray-500">
                              Aggregated across {formsForJob.length} form
                              {formsForJob.length === 1 ? "" : "s"} ·
                              <span className="text-green-700 font-medium ml-1">
                                {stats.totalS} Safe
                              </span>
                              {" · "}
                              <span className="text-red-600 font-medium">
                                {stats.totalC} Concern
                              </span>
                            </p>
                          </div>
                          <div className="flex gap-2 text-[11px] flex-wrap">
                            {stats.safest && stats.safest.S > 0 && (
                              <span className="px-2 py-1 bg-green-50 text-green-700 rounded-full">
                                Top Safest: <b>{stats.safest.label}</b> (
                                {stats.safest.S})
                              </span>
                            )}
                            {stats.concern && stats.concern.C > 0 && (
                              <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full">
                                Top Concern: <b>{stats.concern.label}</b> (
                                {stats.concern.C})
                              </span>
                            )}
                          </div>
                        </div>
                        <div
                          style={{
                            height: Math.max(220, stats.items.length * 28),
                          }}
                        >
                          <ResponsiveContainer width="100%" height="100%">
                            <BarChart
                              data={stats.items}
                              layout="vertical"
                              margin={{ left: 10, right: 20 }}
                            >
                              <CartesianGrid
                                strokeDasharray="3 3"
                                horizontal={false}
                              />
                              <XAxis
                                type="number"
                                allowDecimals={false}
                                fontSize={11}
                              />
                              <YAxis
                                type="category"
                                dataKey="label"
                                fontSize={11}
                                width={140}
                                tickFormatter={(v) =>
                                  v && v.length > 22 ? `${v.slice(0, 20)}…` : v
                                }
                              />
                              <Tooltip />
                              <Legend wrapperStyle={{ fontSize: 11 }} />
                              <Bar
                                dataKey="S"
                                name="Safe"
                                fill="#16A34A"
                                radius={[0, 4, 4, 0]}
                              />
                              <Bar
                                dataKey="C"
                                name="Concern"
                                fill="#DC2626"
                                radius={[0, 4, 4, 0]}
                              />
                            </BarChart>
                          </ResponsiveContainer>
                        </div>
                      </div>
                    ),
                )}
              </div>

              {formsForJob.length === 0 ? (
                <EmptyState message="No BBS forms found for this job." />
              ) : (
                formsForJob.map((r) => {
                  const concerns = [];
                  [
                    "PPE",
                    "Body Position",
                    "Work Environment",
                    "Work Procedure",
                  ].forEach((k) => {
                    const arr = r.bbs_meta?.[k];
                    if (Array.isArray(arr)) {
                      arr.forEach((it) => {
                        if (it.answer === "C" && it.label)
                          concerns.push(it.label);
                      });
                    }
                  });
                  return (
                    <div
                      key={r.id}
                      className="flex items-center justify-between gap-3 border border-gray-200 rounded-lg px-3 py-2 hover:bg-gray-50"
                    >
                      <div className="text-left min-w-0 shrink-0">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {r.observer_name || r.emp_name || `BBS #${r.id}`}
                        </div>
                        <div className="text-xs text-gray-500 truncate">
                          {r.company_name ? `${r.company_name} · ` : ""}
                          {r.filled_date
                            ? dayjs(r.filled_date).format("MM-DD-YYYY")
                            : r.created_at
                            ? dayjs(r.created_at).format("MM-DD-YYYY")
                            : "—"}
                          {r.method ? ` · ${r.method}` : ""}
                        </div>
                      </div>

                      <div className="flex-1 min-w-0 flex flex-wrap gap-1 justify-start">
                        {concerns.length === 0 ? (
                          <span className="text-[11px] text-gray-400 italic">
                            No concerns
                          </span>
                        ) : (
                          concerns.map((label, i) => (
                            <span
                              key={`${label}-${i}`}
                              className="text-[11px] font-medium text-red-700 bg-red-50 border border-red-100 rounded-full px-2 py-0.5"
                              title={label}
                            >
                              {label}
                            </span>
                          ))
                        )}
                      </div>

                      <button
                        onClick={() => handleViewBbsForm(r.id)}
                        className="ml-3 inline-flex items-center gap-1 text-[#155DFC] text-xs font-medium hover:underline shrink-0"
                      >
                        <RemoveRedEyeIcon style={{ fontSize: 16 }} />
                        View
                      </button>
                    </div>
                  );
                })
              )}
            </div>
          </div>
        </div>
      )}

      <Backdrop open={viewLoading} sx={{ zIndex: 9999, color: "#fff" }}>
        <CircularProgress color="inherit" />
      </Backdrop>

      {viewRow && (
        <ViewFormModal
          setFormView={() => setViewRow(null)}
          rowData={viewRow}
          formType={viewRow.form_type}
        />
      )}
    </div>
  );
};

// ---------------------------------------------------------------------------
// Driver Log (WDS) — weekly driver log analytics
// ---------------------------------------------------------------------------

const driverNameOf = (r) => r.emp_name || "Unknown Driver";

const unitNumberOf = (r) => r.wds_meta?.unit_number || null;

const milesOf = (r) => Number(r.wds_meta?.total_miles) || 0;

const DriverLogData = ({ rows }) => {
  const wdsAll = rows.filter((r) => r.form_type === "WDS");

  const [unitQuery, setUnitQuery] = useState("");
  const [jobQuery, setJobQuery] = useState("");
  const [companyId, setCompanyId] = useState("");
  const [unitOpen, setUnitOpen] = useState(false);
  const [jobOpen, setJobOpen] = useState(false);
  const [companies, setCompanies] = useState([]);
  const [selectedUnit, setSelectedUnit] = useState(null); // { unit, ... }
  const [selectedIssue, setSelectedIssue] = useState(null); // issue label string
  const [issueFormSearch, setIssueFormSearch] = useState("");
  const [selectedJobName, setSelectedJobName] = useState(null);
  const [jobFormSearch, setJobFormSearch] = useState("");
  const [viewWdsRow, setViewWdsRow] = useState(null);
  const [wdsViewLoading, setWdsViewLoading] = useState(false);
  const unitWrapRef = useRef(null);

  const handleViewWdsForm = async (rowid) => {
    try {
      setWdsViewLoading(true);
      const resp = await api.get("/v1/forms/get_by_id", { params: { rowid } });
      if (resp.status === 200 && resp.data?.[0]) {
        setViewWdsRow(resp.data[0]);
      }
    } catch (e) {
      console.error("Failed to fetch WDS form", e);
    } finally {
      setWdsViewLoading(false);
    }
  };

  const formsForUnit = useMemo(() => {
    if (!selectedUnit) return [];
    return wdsAll.filter(
      (r) => (r.wds_meta?.unit_number || "").trim() === selectedUnit.unit,
    );
  }, [wdsAll, selectedUnit]);

  // Universal search across every common WDS field — supports terms like
  // "252", "construction", "mike", ">15 mpg", "100 gal", "$200", "2026-01-15".
  // Each space-separated token must match somewhere in the haystack so users
  // can combine filters like "construction 252 mpg".
  const buildHaystack = (r) => {
    const m = r.wds_meta || {};
    const parts = [
      r.emp_name,
      r.observer_name,
      r.company_name,
      r.method,
      r.filled_date,
      r.created_at,
      r.form_type,
      m.unit_number,
      m.fuel_allocation,
      m.total_miles != null ? `${m.total_miles} mi miles` : "",
      m.total_gallons != null ? `${m.total_gallons} gal gallons` : "",
      m.total_amount != null ? `$${m.total_amount} amount spent` : "",
      m.avg_mpg != null ? `${m.avg_mpg} mpg` : "",
      m.fillup_count != null ? `${m.fillup_count} fillups` : "",
      m.log_count != null ? `${m.log_count} logs` : "",
      ...(m.jobs || []).map(String),
      ...(m.issues || []).map(String),
    ];
    return parts.filter(Boolean).join(" ").toLowerCase();
  };
  const matchesQuery = (r, q) => {
    const trimmed = q.trim().toLowerCase();
    if (!trimmed) return true;
    const hay = buildHaystack(r);
    return trimmed.split(/\s+/).every((tok) => hay.includes(tok));
  };

  const [unitFormSearch, setUnitFormSearch] = useState("");
  useEffect(() => {
    setUnitFormSearch("");
  }, [selectedUnit]);
  const formsForUnitFiltered = useMemo(() => {
    if (!unitFormSearch.trim()) return formsForUnit;
    return formsForUnit.filter((r) => matchesQuery(r, unitFormSearch));
  }, [formsForUnit, unitFormSearch]);

  // Forms where the selected inspection issue was reported
  const formsForIssue = useMemo(() => {
    if (!selectedIssue) return [];
    return wdsAll.filter((r) =>
      (r.wds_meta?.issues || []).some(
        (it) => String(it).trim() === selectedIssue,
      ),
    );
  }, [wdsAll, selectedIssue]);
  useEffect(() => {
    setIssueFormSearch("");
  }, [selectedIssue]);
  const formsForIssueFiltered = useMemo(() => {
    if (!issueFormSearch.trim()) return formsForIssue;
    return formsForIssue.filter((r) => matchesQuery(r, issueFormSearch));
  }, [formsForIssue, issueFormSearch]);

  // Forms filed under the selected job (Driver Log Top 10 Jobs popup)
  const formsForJobName = useMemo(() => {
    if (!selectedJobName) return [];
    return wdsAll.filter((r) =>
      (r.wds_meta?.jobs || []).some(
        (j) => String(j).trim() === selectedJobName,
      ),
    );
  }, [wdsAll, selectedJobName]);
  useEffect(() => {
    setJobFormSearch("");
  }, [selectedJobName]);
  const formsForJobFiltered = useMemo(() => {
    if (!jobFormSearch.trim()) return formsForJobName;
    return formsForJobName.filter((r) => matchesQuery(r, jobFormSearch));
  }, [formsForJobName, jobFormSearch]);
  const jobWrapRef = useRef(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const resp = await api.get("/v1/timesheets/company-details");
        if (!cancelled && resp.status === 200 && Array.isArray(resp.data)) {
          setCompanies(resp.data);
        }
      } catch (e) {
        console.error("Failed to load companies", e);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  useEffect(() => {
    const onClick = (e) => {
      if (unitWrapRef.current && !unitWrapRef.current.contains(e.target))
        setUnitOpen(false);
      if (jobWrapRef.current && !jobWrapRef.current.contains(e.target))
        setJobOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Distinct units & jobs (for autocomplete dropdowns)
  const allUnits = useMemo(() => {
    const set = new Set();
    wdsAll.forEach((r) => {
      const u = (r.wds_meta?.unit_number || "").trim();
      if (u) set.add(u);
    });
    return Array.from(set).sort();
  }, [wdsAll]);

  const allJobs = useMemo(() => {
    const set = new Set();
    wdsAll.forEach((r) => {
      (r.wds_meta?.jobs || []).forEach((j) => {
        const v = String(j || "").trim();
        if (v) set.add(v);
      });
    });
    return Array.from(set).sort();
  }, [wdsAll]);

  // Apply filters
  const wds = useMemo(() => {
    const u = unitQuery.trim().toLowerCase();
    const j = jobQuery.trim().toLowerCase();
    return wdsAll.filter((r) => {
      if (companyId && String(r.companyid) !== String(companyId)) return false;
      if (u) {
        const unit = (r.wds_meta?.unit_number || "").toLowerCase();
        if (!unit.includes(u)) return false;
      }
      if (j) {
        const jobs = (r.wds_meta?.jobs || []).map((x) =>
          String(x).toLowerCase(),
        );
        if (!jobs.some((x) => x.includes(j))) return false;
      }
      return true;
    });
  }, [wdsAll, unitQuery, jobQuery, companyId]);

  const unitMatches = useMemo(() => {
    const q = unitQuery.trim().toLowerCase();
    return q
      ? allUnits.filter((u) => u.toLowerCase().includes(q)).slice(0, 50)
      : allUnits.slice(0, 50);
  }, [allUnits, unitQuery]);

  const jobMatches = useMemo(() => {
    const q = jobQuery.trim().toLowerCase();
    return q
      ? allJobs.filter((j) => j.toLowerCase().includes(q)).slice(0, 50)
      : allJobs.slice(0, 50);
  }, [allJobs, jobQuery]);

  const monthly = useMemo(() => buildMonthlyTrend(wds, 6), [wds]);

  const perCompany = useMemo(() => {
    const map = {};
    wds.forEach((r) => {
      const name = (r.company_name || "").trim();
      if (!name) return;
      map[name] = (map[name] || 0) + 1;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 8)
      .map(([name, value]) => ({ name, value }));
  }, [wds]);

  const topDrivers = useMemo(() => {
    const m = countBy(wds, (r) => driverNameOf(r));
    return Object.entries(m)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 6);
  }, [wds]);

  // Per-unit aggregates: miles, gallons, amount, avg MPG
  const perUnit = useMemo(() => {
    const map = {};
    wds.forEach((r) => {
      const u = (r.wds_meta?.unit_number || "").trim();
      if (!u) return;
      if (!map[u]) {
        map[u] = {
          unit: u,
          logs: 0,
          miles: 0,
          gallons: 0,
          amount: 0,
          mpgSum: 0,
          mpgN: 0,
        };
      }
      const m = r.wds_meta || {};
      map[u].logs += 1;
      map[u].miles += Number(m.total_miles) || 0;
      map[u].gallons += Number(m.total_gallons) || 0;
      map[u].amount += Number(m.total_amount) || 0;
      if (Number(m.avg_mpg) > 0) {
        map[u].mpgSum += Number(m.avg_mpg);
        map[u].mpgN += 1;
      }
    });
    return Object.values(map).map((u) => ({
      ...u,
      avgGallons: u.logs ? +(u.gallons / u.logs).toFixed(1) : 0,
      avgAmount: u.logs ? +(u.amount / u.logs).toFixed(2) : 0,
      avgMpg: u.mpgN ? +(u.mpgSum / u.mpgN).toFixed(2) : 0,
    }));
  }, [wds]);

  // Vehicle inspection issues — count of "No" answers per item across forms
  const issuesByItem = useMemo(() => {
    const map = {};
    wds.forEach((r) => {
      (r.wds_meta?.issues || []).forEach((label) => {
        const key = String(label || "").trim();
        if (!key) return;
        map[key] = (map[key] || 0) + 1;
      });
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .map(([name, value]) => ({ name, value }));
  }, [wds]);

  const totalIssues = useMemo(
    () => issuesByItem.reduce((a, x) => a + x.value, 0),
    [issuesByItem],
  );
  const flaggedSubmissions = useMemo(
    () => wds.filter((r) => (r.wds_meta?.issues || []).length > 0).length,
    [wds],
  );

  // Driver-log anomalies
  const errorsByType = useMemo(() => {
    const sums = {
      "Past Date (>1 day)": 0,
      "Negative Miles": 0,
      "Future Date": 0,
      "Negative Mileage": 0,
    };
    wds.forEach((r) => {
      const a = r.wds_meta?.anomalies || {};
      sums["Past Date (>1 day)"] += Number(a.past_date_gt1d) || 0;
      sums["Negative Miles"] += Number(a.negative_miles) || 0;
      sums["Future Date"] += Number(a.future_date) || 0;
      sums["Negative Mileage"] += Number(a.negative_mileage) || 0;
    });
    return Object.entries(sums)
      .filter(([, v]) => v > 0)
      .map(([name, value]) => ({ name, value }));
  }, [wds]);

  const totalErrors = useMemo(
    () => errorsByType.reduce((a, x) => a + x.value, 0),
    [errorsByType],
  );

  const errorsByUnit = useMemo(() => {
    const map = {};
    wds.forEach((r) => {
      const u = (r.wds_meta?.unit_number || "").trim();
      if (!u) return;
      const a = r.wds_meta?.anomalies || {};
      const total =
        (Number(a.past_date_gt1d) || 0) +
        (Number(a.negative_miles) || 0) +
        (Number(a.future_date) || 0) +
        (Number(a.negative_mileage) || 0);
      if (total <= 0) return;
      map[u] = (map[u] || 0) + total;
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 20)
      .map(([unit, value]) => ({ unit, value }));
  }, [wds]);

  const topJobs = useMemo(() => {
    const map = {};
    wds.forEach((r) => {
      (r.wds_meta?.jobs || []).forEach((j) => {
        const v = String(j || "").trim();
        if (!v) return;
        map[v] = (map[v] || 0) + 1;
      });
    });
    return Object.entries(map)
      .sort((a, b) => b[1] - a[1])
      .slice(0, 10)
      .map(([name, value]) => ({ name, value }));
  }, [wds]);

  const topUnitsByLogs = useMemo(
    () => [...perUnit].sort((a, b) => b.logs - a.logs).slice(0, 10),
    [perUnit],
  );
  const topUnitsByMiles = useMemo(
    () => [...perUnit].sort((a, b) => b.miles - a.miles).slice(0, 10),
    [perUnit],
  );
  const topUnitsByMpg = useMemo(
    () =>
      [...perUnit]
        .filter((u) => u.avgMpg > 0)
        .sort((a, b) => b.avgMpg - a.avgMpg)
        .slice(0, 10),
    [perUnit],
  );
  const topUnitsBySpend = useMemo(
    () => [...perUnit].sort((a, b) => b.amount - a.amount).slice(0, 10),
    [perUnit],
  );

  const perMethod = useMemo(() => {
    const m = countBy(wds, (r) => r.method || "Manual");
    return Object.entries(m).map(([k, v]) => ({ name: k, value: v }));
  }, [wds]);

  const totalLogs = wds.length;
  const uniqueDrivers = new Set(wds.map(driverNameOf)).size;
  const uniqueUnits = new Set(wds.map(unitNumberOf).filter(Boolean)).size;
  const totalMiles = useMemo(
    () => Math.round(wds.reduce((acc, r) => acc + milesOf(r), 0)),
    [wds],
  );
  const totalGallons = useMemo(
    () =>
      +wds
        .reduce((a, r) => a + (Number(r.wds_meta?.total_gallons) || 0), 0)
        .toFixed(1),
    [wds],
  );
  const totalSpent = useMemo(
    () =>
      +wds
        .reduce((a, r) => a + (Number(r.wds_meta?.total_amount) || 0), 0)
        .toFixed(2),
    [wds],
  );
  const overallMpg = useMemo(() => {
    if (!totalGallons) return 0;
    return +(totalMiles / totalGallons).toFixed(2);
  }, [totalMiles, totalGallons]);

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      {/* Filters */}
      <Card className="lg:col-span-3">
        <div className="flex items-end gap-3 flex-wrap">
          <div className="flex-1 min-w-[180px]">
            <label className="block text-[11px] uppercase text-gray-500 mb-1 text-left">
              Search by Company
            </label>
            <select
              value={companyId}
              onChange={(e) => setCompanyId(e.target.value)}
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-gray-400"
            >
              <option value="">All Companies</option>
              {companies.map((c) => (
                <option key={c.id} value={c.id}>
                  {c.name}
                </option>
              ))}
            </select>
          </div>
          <div className="flex-1 min-w-[180px] relative" ref={unitWrapRef}>
            <label className="block text-[11px] uppercase text-gray-500 mb-1 text-left">
              Search by Unit #
            </label>
            <input
              type="text"
              value={unitQuery}
              onChange={(e) => {
                setUnitQuery(e.target.value);
                setUnitOpen(true);
              }}
              onFocus={() => setUnitOpen(true)}
              placeholder="e.g. 252"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-gray-400"
            />
            {unitOpen && unitMatches.length > 0 && (
              <ul className="absolute left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-40">
                {unitMatches.map((u) => (
                  <li key={u}>
                    <button
                      type="button"
                      onClick={() => {
                        setUnitQuery(u);
                        setUnitOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      {u}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          <div className="flex-1 min-w-[180px] relative" ref={jobWrapRef}>
            <label className="block text-[11px] uppercase text-gray-500 mb-1 text-left">
              Search by Job
            </label>
            <input
              type="text"
              value={jobQuery}
              onChange={(e) => {
                setJobQuery(e.target.value);
                setJobOpen(true);
              }}
              onFocus={() => setJobOpen(true)}
              placeholder="e.g. 9990"
              className="w-full px-3 py-1.5 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-gray-400"
            />
            {jobOpen && jobMatches.length > 0 && (
              <ul className="absolute left-0 right-0 top-full mt-1 max-h-60 overflow-y-auto bg-white border border-gray-200 rounded-lg shadow-lg z-40">
                {jobMatches.map((j) => (
                  <li key={j}>
                    <button
                      type="button"
                      onClick={() => {
                        setJobQuery(j);
                        setJobOpen(false);
                      }}
                      className="w-full text-left px-3 py-1.5 text-sm hover:bg-gray-50 border-b border-gray-100 last:border-b-0"
                    >
                      {j}
                    </button>
                  </li>
                ))}
              </ul>
            )}
          </div>
          {(unitQuery || jobQuery || companyId) && (
            <button
              onClick={() => {
                setUnitQuery("");
                setJobQuery("");
                setCompanyId("");
              }}
              className="text-xs px-3 py-1.5 rounded-md border border-gray-200 hover:bg-gray-100 text-gray-600"
            >
              Clear Filters
            </button>
          )}
          <div className="text-xs text-gray-500 ml-auto">
            Showing <b>{wds.length}</b> of {wdsAll.length} logs
          </div>
        </div>
      </Card>

      {/* Summary tiles */}
      <Card className="lg:col-span-3">
        <SectionTitle
          title="Driver Log Overview"
          subtitle="Weekly Driver & Safety log activity across the selected range"
        />
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3">
          {[
            { label: "Total Logs", value: totalLogs, color: "#E17100" },
            { label: "Unique Drivers", value: uniqueDrivers, color: "#155DFC" },
            { label: "Active Units", value: uniqueUnits, color: "#00A63E" },
            // { label: "Total Miles",    value: totalMiles.toLocaleString(), color: "#9333EA" },
            {
              label: "Total Gallons",
              value: totalGallons.toLocaleString(),
              color: "#0EA5E9",
            },
            {
              label: "Total Spent",
              value: `$${totalSpent.toLocaleString()}`,
              color: "#F43F5E",
            },
            {
              label: "Avg MPG",
              value: overallMpg,
              color: "#10B981",
              tip: "Miles Per Gallon",
            },
            {
              label: "Issues",
              value: totalIssues,
              color: "#F97316",
              tip: 'Vehicle Safety Check items reported as "No"',
            },
            {
              label: "Flagged %",
              value: wds.length
                ? `${((flaggedSubmissions / wds.length) * 100).toFixed(1)}%`
                : "0%",
              color: "#DC2626",
              tip: "% of forms with at least one issue",
            },
            {
              label: "Error Count",
              value: totalErrors,
              color: "#06B6D4",
              tip: "Driver-log anomalies (negative miles / future date / past date / negative mileage)",
            },
          ].map((t) => (
            <div
              key={t.label}
              title={t.tip || ""}
              className="rounded-xl border border-gray-100 bg-gray-50 p-4 text-left"
              style={{ borderLeft: `4px solid ${t.color}` }}
            >
              <div className="text-xs uppercase tracking-wider text-gray-500">
                {t.label}
                {t.tip && (
                  <span
                    className="ml-1 text-gray-400 cursor-help"
                    title={t.tip}
                  >
                    ⓘ
                  </span>
                )}
              </div>
              <div className="mt-1 text-2xl font-bold text-[#0A0A0A]">
                {t.value}
              </div>
            </div>
          ))}
        </div>
      </Card>

      {/* Per-Unit comparison charts — 2 per row */}
      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        {[
          {
            title: "Top Units by Logs",
            subtitle: "Most active trucks · click a bar to view forms",
            data: topUnitsByLogs,
            dataKey: "logs",
            fill: "#E17100",
            fmt: (v) => v,
            xLabel: "Logs",
            clickable: true,
          },
          {
            title: "Top Units by Miles",
            subtitle: "Total miles driven · click a bar to view forms",
            data: topUnitsByMiles,
            dataKey: "miles",
            fill: "#9333EA",
            fmt: (v) => Math.round(v).toLocaleString(),
            xLabel: "Miles",
            clickable: true,
          },
          {
            title: "Avg MPG by Unit",
            subtitle: "Miles Per Gallon · click a bar to view forms",
            data: topUnitsByMpg,
            dataKey: "avgMpg",
            fill: "#10B981",
            fmt: (v) => v,
            tip: "Miles Per Gallon",
            xLabel: "MPG",
            clickable: true,
          },
          {
            title: "Top Units by Spend",
            subtitle: "Total fuel $ across logs · click a bar to view forms",
            data: topUnitsBySpend,
            dataKey: "amount",
            fill: "#F43F5E",
            fmt: (v) => `$${Math.round(v).toLocaleString()}`,
            xLabel: "Spend ($)",
            clickable: true,
          },
        ].map((cfg) => (
          <Card key={cfg.title}>
            <div title={cfg.tip || ""}>
              <SectionTitle title={cfg.title} subtitle={cfg.subtitle} />
            </div>
            {cfg.data.length === 0 ? (
              <EmptyState />
            ) : (
              <div style={{ height: Math.max(260, cfg.data.length * 32) }}>
                <ResponsiveContainer width="100%" height="100%">
                  <BarChart
                    data={cfg.data}
                    layout="vertical"
                    margin={{ left: 30, right: 80, bottom: 20 }}
                  >
                    <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                    <XAxis
                      type="number"
                      allowDecimals={false}
                      fontSize={11}
                      label={{
                        value: cfg.xLabel,
                        position: "insideBottom",
                        offset: -8,
                        fontSize: 11,
                        fill: "#6B7280",
                      }}
                    />
                    <YAxis
                      type="category"
                      dataKey="unit"
                      fontSize={11}
                      width={100}
                      label={{
                        value: "Truck Unit #",
                        angle: -90,
                        position: "insideLeft",
                        offset: -5,
                        fontSize: 11,
                        fill: "#6B7280",
                      }}
                    />
                    <Tooltip formatter={(v) => cfg.fmt(v)} />
                    <Bar
                      dataKey={cfg.dataKey}
                      fill={cfg.fill}
                      radius={[0, 6, 6, 0]}
                      cursor={cfg.clickable ? "pointer" : "default"}
                      onClick={
                        cfg.clickable
                          ? (d) => {
                              const row = d?.payload || d;
                              if (row && row.unit) setSelectedUnit(row);
                            }
                          : undefined
                      }
                    >
                      <LabelList
                        dataKey={cfg.dataKey}
                        position="right"
                        style={{ fontSize: 10, fill: "#374151" }}
                        formatter={(v) => cfg.fmt(v)}
                      />
                    </Bar>
                  </BarChart>
                </ResponsiveContainer>
              </div>
            )}
          </Card>
        ))}
      </div>

      {/* Maintenance — Amount of Issues */}

      {/* Anomalies — User Entry Errors per Unit + Donut by Type */}
      <div className="lg:col-span-4 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <div className="flex items-center justify-between flex-wrap gap-2 mb-2">
            <SectionTitle
              title="Amount of Issues"
              subtitle='Vehicle Safety Check items reported as "No" · click a bar to view forms'
            />
            <div className="flex gap-2 text-[11px]">
              <span className="px-2 py-1 bg-orange-50 text-orange-700 rounded-full">
                Total Issues: <b>{totalIssues}</b>
              </span>
              <span className="px-2 py-1 bg-red-50 text-red-700 rounded-full">
                Flagged Submissions: <b>{flaggedSubmissions}</b>
                {wds.length
                  ? ` (${((flaggedSubmissions / wds.length) * 100).toFixed(
                      1,
                    )}%)`
                  : ""}
              </span>
            </div>
          </div>
          {issuesByItem.length === 0 ? (
            <EmptyState message="No inspection issues reported." />
          ) : (
            <div style={{ height: Math.max(320, issuesByItem.length * 26) }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={issuesByItem}
                  layout="vertical"
                  margin={{ left: 30, right: 70, bottom: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis
                    type="number"
                    allowDecimals={false}
                    fontSize={11}
                    label={{
                      value: "Failures",
                      position: "insideBottom",
                      offset: -8,
                      fontSize: 11,
                      fill: "#6B7280",
                    }}
                  />
                  <YAxis
                    type="category"
                    dataKey="name"
                    fontSize={11}
                    width={180}
                    tickFormatter={(v) =>
                      v && v.length > 26 ? `${v.slice(0, 24)}…` : v
                    }
                    label={{
                      value: "Issue",
                      angle: -90,
                      position: "insideLeft",
                      offset: -5,
                      fontSize: 11,
                      fill: "#6B7280",
                    }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill="#F97316"
                    radius={[0, 6, 6, 0]}
                    cursor="pointer"
                    onClick={(d) => {
                      const row = d?.payload || d;
                      if (row && row.name) setSelectedIssue(row.name);
                    }}
                  >
                    <LabelList
                      dataKey="value"
                      position="right"
                      style={{ fontSize: 10, fill: "#374151" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
        <Card>
          <SectionTitle
            title="User Entry Errors"
            subtitle="Anomaly counts per Truck Unit · click a bar to view forms"
          />
          {errorsByUnit.length === 0 ? (
            <EmptyState message="No anomalies detected." />
          ) : (
            <div style={{ height: 360 }}>
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={errorsByUnit}
                  margin={{ left: 20, right: 20, bottom: 40, top: 10 }}
                >
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="unit"
                    fontSize={11}
                    interval={0}
                    angle={-25}
                    textAnchor="end"
                    height={60}
                    label={{
                      value: "Truck Unit Number",
                      position: "insideBottom",
                      offset: -2,
                      fontSize: 11,
                      fill: "#6B7280",
                    }}
                  />
                  <YAxis
                    allowDecimals={false}
                    fontSize={11}
                    label={{
                      value: "Error Count",
                      angle: -90,
                      position: "insideLeft",
                      offset: 10,
                      fontSize: 11,
                      fill: "#6B7280",
                    }}
                  />
                  <Tooltip />
                  <Bar
                    dataKey="value"
                    fill="#EF4444"
                    radius={[6, 6, 0, 0]}
                    cursor="pointer"
                    onClick={(d) => {
                      const row = d?.payload || d;
                      if (row && row.unit) {
                        const u = perUnit.find((x) => x.unit === row.unit);
                        setSelectedUnit(
                          u || { unit: row.unit, miles: 0, gallons: 0, amount: 0, avgMpg: 0 },
                        );
                      }
                    }}
                  >
                    <LabelList
                      dataKey="value"
                      position="top"
                      style={{ fontSize: 10, fill: "#374151" }}
                    />
                  </Bar>
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
        {/* <Card>
          <SectionTitle title="Error Type" subtitle="Distribution of anomaly types" />
          <div className="text-center mb-2">
            <div className="text-3xl font-bold text-gray-900">{totalErrors.toLocaleString()}</div>
            <div className="text-xs text-gray-500 uppercase tracking-wider">Error Count</div>
          </div>
          {errorsByType.length === 0 ? (
            <EmptyState message="No anomalies detected." />
          ) : (
            <div style={{ height: 240 }}>
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie
                    data={errorsByType}
                    dataKey="value"
                    nameKey="name"
                    innerRadius={50}
                    outerRadius={85}
                    paddingAngle={2}
                    label={(d) => `${((d.value / totalErrors) * 100).toFixed(1)}%`}
                    fontSize={10}
                  >
                    {errorsByType.map((_, i) => (
                      <Cell
                        key={i}
                        fill={["#06B6D4", "#0F172A", "#94A3B8", "#3B82F6"][i % 4]}
                      />
                    ))}
                  </Pie>
                  <Tooltip />
                  <Legend wrapperStyle={{ fontSize: 11 }} />
                </PieChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card> */}
      </div>

      {/* Top 10 Jobs + Top Drivers row */}
      <Card className="lg:col-span-2">
        <SectionTitle
          title="Top 10 Jobs"
          subtitle="Most frequent jobs across driver log entries · click a bar to view forms"
        />
        {topJobs.length === 0 ? (
          <EmptyState />
        ) : (
          <div style={{ height: 360 }}>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart
                data={topJobs}
                margin={{ left: 20, right: 20, bottom: 40, top: 10 }}
              >
                <defs>
                  <linearGradient id="jobsGrad" x1="0" y1="0" x2="0" y2="1">
                    <stop offset="0%" stopColor="#2563EB" />
                    <stop offset="100%" stopColor="#06B6D4" />
                  </linearGradient>
                </defs>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis
                  dataKey="name"
                  fontSize={11}
                  interval={0}
                  angle={-25}
                  textAnchor="end"
                  height={60}
                  tickFormatter={(v) =>
                    v && v.length > 14 ? `${v.slice(0, 12)}…` : v
                  }
                  label={{
                    value: "Job",
                    position: "insideBottom",
                    offset: -2,
                    fontSize: 11,
                    fill: "#6B7280",
                  }}
                />
                <YAxis
                  allowDecimals={false}
                  fontSize={11}
                  label={{
                    value: "Logs",
                    angle: -90,
                    position: "insideLeft",
                    offset: 10,
                    fontSize: 11,
                    fill: "#6B7280",
                  }}
                />
                <Tooltip />
                <Bar
                  dataKey="value"
                  fill="url(#jobsGrad)"
                  radius={[8, 8, 0, 0]}
                  cursor="pointer"
                  onClick={(d) => {
                    const row = d?.payload || d;
                    if (row && row.name) setSelectedJobName(row.name);
                  }}
                >
                  <LabelList
                    dataKey="value"
                    position="top"
                    style={{ fontSize: 10, fill: "#374151" }}
                  />
                </Bar>
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      <Card>
        <SectionTitle title="Top Drivers" subtitle="Most active log filers" />
        {topDrivers.length === 0 ? (
          <EmptyState />
        ) : (
          <ul className="space-y-2 text-sm">
            {topDrivers.map(([name, count], i) => (
              <li
                key={name + i}
                className="flex items-center justify-between border-b border-gray-100 pb-2 last:border-b-0 last:pb-0 text-left"
              >
                <span className="text-gray-800 truncate">{name}</span>
                <span className="text-xs font-semibold text-[#E17100] bg-orange-50 rounded-full px-2 py-0.5">
                  {count}
                </span>
              </li>
            ))}
          </ul>
        )}
      </Card>

      {/* Monthly trend + By Company — 50/50 row */}
      <div className="lg:col-span-3 grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card>
          <SectionTitle
            title="Driver Logs per Month"
            subtitle="Submission volume over the last 6 months"
          />
          {wds.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart data={monthly}>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis dataKey="label" fontSize={11} />
                  <YAxis allowDecimals={false} fontSize={11} />
                  <Tooltip />
                  <Bar dataKey="WDS" fill="#E17100" radius={[6, 6, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>

        {/* Top companies */}
        <Card>
          <SectionTitle
            title="By Company"
            subtitle="Top organizations by driver log volume"
          />
          {perCompany.length === 0 ? (
            <EmptyState />
          ) : (
            <div className="h-[280px]">
              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={perCompany}
                  layout="vertical"
                  margin={{ left: 20 }}
                >
                  <CartesianGrid strokeDasharray="3 3" horizontal={false} />
                  <XAxis type="number" allowDecimals={false} fontSize={11} />
                  <YAxis
                    type="category"
                    dataKey="name"
                    fontSize={11}
                    width={130}
                  />
                  <Tooltip />
                  <Bar dataKey="value" fill="#E17100" radius={[0, 6, 6, 0]} />
                </BarChart>
              </ResponsiveContainer>
            </div>
          )}
        </Card>
      </div>

      {/* Unit detail popup */}
      {selectedUnit && !viewWdsRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900">
                  Unit {selectedUnit.unit}
                </h3>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 font-semibold border border-orange-100">
                    {formsForUnit.length} driver log{formsForUnit.length === 1 ? "" : "s"}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-purple-50 text-purple-700 font-semibold border border-purple-100">
                    Total miles: {Math.round(selectedUnit.miles).toLocaleString()}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-sky-50 text-sky-700 font-semibold border border-sky-100">
                    Total gallons: {selectedUnit.gallons.toFixed(1)}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-rose-50 text-rose-700 font-semibold border border-rose-100">
                    Total spent: ${Math.round(selectedUnit.amount).toLocaleString()}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-emerald-50 text-emerald-700 font-semibold border border-emerald-100">
                    Avg MPG: {selectedUnit.avgMpg}
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedUnit(null)}
                className="text-xs px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                Close
              </button>
            </div>

            <div className="mb-3">
              <input
                type="text"
                value={unitFormSearch}
                onChange={(e) => setUnitFormSearch(e.target.value)}
                placeholder="Search "
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-gray-400"
              />
              {unitFormSearch && (
                <div className="text-[11px] text-gray-500 mt-1">
                  Showing <b>{formsForUnitFiltered.length}</b> of {formsForUnit.length} logs
                </div>
              )}
            </div>

            {formsForUnitFiltered.length === 0 ? (
              <EmptyState message="No forms match the search." />
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                {formsForUnitFiltered.map((r) => {
                  const m = r.wds_meta || {};
                  return (
                    <li key={r.id} className="flex items-center justify-between px-3 py-3 text-left">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {r.emp_name || "Driver —"}
                          {r.company_name ? ` · ${r.company_name}` : ""}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {r.filled_date ? `Date: ${r.filled_date}` : ""}
                          {m.total_miles ? ` · ${Math.round(m.total_miles)} mi` : ""}
                          {m.total_gallons ? ` · ${m.total_gallons} gal` : ""}
                          {m.total_amount ? ` · $${m.total_amount}` : ""}
                          {m.avg_mpg ? ` · ${m.avg_mpg} MPG` : ""}
                          {r.method ? ` · ${r.method}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewWdsForm(r.id)}
                        className="ml-3 inline-flex items-center gap-1 text-[#155DFC] text-xs font-medium hover:underline shrink-0"
                      >
                        <RemoveRedEyeIcon style={{ fontSize: 16 }} />
                        View
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Issue detail popup */}
      {selectedIssue && !viewWdsRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900">
                  Issue: {selectedIssue}
                </h3>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 font-semibold border border-orange-100">
                    {formsForIssue.length} driver log{formsForIssue.length === 1 ? "" : "s"}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-red-50 text-red-700 font-semibold border border-red-100">
                    Reported as "No"
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedIssue(null)}
                className="text-xs px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                Close
              </button>
            </div>
{/* anything — driver, company, unit, mi, gal, $, MPG, date… */}
            <div className="mb-3">
              <input
                type="text"
                value={issueFormSearch}
                onChange={(e) => setIssueFormSearch(e.target.value)}
                placeholder="Search"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-gray-400"
              />
              {issueFormSearch && (
                <div className="text-[11px] text-gray-500 mt-1">
                  Showing <b>{formsForIssueFiltered.length}</b> of {formsForIssue.length} logs
                </div>
              )}
            </div>

            {formsForIssueFiltered.length === 0 ? (
              <EmptyState message="No forms match the search." />
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                {formsForIssueFiltered.map((r) => {
                  const m = r.wds_meta || {};
                  return (
                    <li key={r.id} className="flex items-center justify-between px-3 py-3 text-left">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {r.emp_name || "Driver —"}
                          {r.company_name ? ` · ${r.company_name}` : ""}
                          {m.unit_number ? ` · Unit ${m.unit_number}` : ""}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {r.filled_date ? `Date: ${r.filled_date}` : ""}
                          {(m.issues || []).length
                            ? ` · ${m.issues.length} issue${m.issues.length === 1 ? "" : "s"} reported`
                            : ""}
                          {r.method ? ` · ${r.method}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewWdsForm(r.id)}
                        className="ml-3 inline-flex items-center gap-1 text-[#155DFC] text-xs font-medium hover:underline shrink-0"
                      >
                        <RemoveRedEyeIcon style={{ fontSize: 16 }} />
                        View
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      {/* Job detail popup (Driver Log Top 10 Jobs) */}
      {selectedJobName && !viewWdsRow && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9998] px-4">
          <div className="bg-white rounded-xl shadow-2xl w-full max-w-6xl p-6 max-h-[92vh] overflow-y-auto">
            <div className="flex items-start justify-between mb-3 flex-wrap gap-2">
              <div className="text-left">
                <h3 className="text-lg font-semibold text-gray-900">
                  Job: {selectedJobName}
                </h3>
                <div className="mt-2 flex flex-wrap gap-2 text-[11px]">
                  <span className="px-2.5 py-1 rounded-full bg-orange-50 text-orange-700 font-semibold border border-orange-100">
                    {formsForJobName.length} driver log{formsForJobName.length === 1 ? "" : "s"}
                  </span>
                  <span className="px-2.5 py-1 rounded-full bg-blue-50 text-blue-700 font-semibold border border-blue-100">
                    Job referenced
                  </span>
                </div>
              </div>
              <button
                onClick={() => setSelectedJobName(null)}
                className="text-xs px-3 py-1.5 rounded-md bg-red-600 hover:bg-red-700 text-white font-medium"
              >
                Close
              </button>
            </div>

            <div className="mb-3">
              <input
                type="text"
                value={jobFormSearch}
                onChange={(e) => setJobFormSearch(e.target.value)}
                placeholder="Search"
                className="w-full px-3 py-2 text-sm border border-gray-200 rounded-lg bg-white outline-none focus:border-gray-400"
              />
              {jobFormSearch && (
                <div className="text-[11px] text-gray-500 mt-1">
                  Showing <b>{formsForJobFiltered.length}</b> of {formsForJobName.length} logs
                </div>
              )}
            </div>

            {formsForJobFiltered.length === 0 ? (
              <EmptyState message="No forms match the search." />
            ) : (
              <ul className="divide-y divide-gray-100 border border-gray-100 rounded-lg">
                {formsForJobFiltered.map((r) => {
                  const m = r.wds_meta || {};
                  return (
                    <li key={r.id} className="flex items-center justify-between px-3 py-3 text-left">
                      <div className="min-w-0 flex-1">
                        <div className="text-sm font-medium text-gray-900 truncate">
                          {r.emp_name || "Driver —"}
                          {r.company_name ? ` · ${r.company_name}` : ""}
                          {m.unit_number ? ` · Unit ${m.unit_number}` : ""}
                        </div>
                        <div className="text-xs text-gray-500 mt-0.5">
                          {r.filled_date ? `Date: ${r.filled_date}` : ""}
                          {m.total_miles ? ` · ${Math.round(m.total_miles)} mi` : ""}
                          {m.total_gallons ? ` · ${m.total_gallons} gal` : ""}
                          {m.total_amount ? ` · $${m.total_amount}` : ""}
                          {m.avg_mpg ? ` · ${m.avg_mpg} MPG` : ""}
                          {r.method ? ` · ${r.method}` : ""}
                        </div>
                      </div>
                      <button
                        onClick={() => handleViewWdsForm(r.id)}
                        className="ml-3 inline-flex items-center gap-1 text-[#155DFC] text-xs font-medium hover:underline shrink-0"
                      >
                        <RemoveRedEyeIcon style={{ fontSize: 16 }} />
                        View
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </div>
      )}

      <Backdrop open={wdsViewLoading} sx={{ zIndex: 9999 }}>
        <CircularProgress />
      </Backdrop>
      {viewWdsRow && (
        <WDSFormModal
          rowData={viewWdsRow}
          setFormView={() => setViewWdsRow(null)}
        />
      )}
    </div>
  );
};

const TREND_GRANULARITIES = [
  { key: "daily", label: "Daily" },
  { key: "weekly", label: "Weekly" },
  { key: "monthly", label: "Monthly" },
];

const bucketKeyForRow = (row, granularity) => {
  const d = getRowDate(row);
  if (!d) return null;
  if (granularity === "monthly") return d.format("YYYY-MM");
  if (granularity === "weekly") return d.startOf("week").format("YYYY-MM-DD");
  return d.format("YYYY-MM-DD");
};

const Trends = ({ allRows }) => {
  const [granularity, setGranularity] = useState("monthly");
  const [visibleTypes, setVisibleTypes] = useState(() => new Set(FORM_CODES));
  const [drilldown, setDrilldown] = useState(null);
  const toggleType = (code) =>
    setVisibleTypes((prev) => {
      const next = new Set(prev);
      if (next.has(code)) next.delete(code);
      else next.add(code);
      return next;
    });
  const yearStart = dayjs().startOf("year");
  const monthStart = dayjs().startOf("month");
  const twelveWeeksAgo = dayjs().subtract(12, "week").startOf("week");

  // Year-to-date rows for the main chart.
  const yearRows = useMemo(
    () =>
      allRows.filter((r) => {
        const d = getRowDate(r);
        return (
          d &&
          FORM_CODES.includes(r.form_type) &&
          (d.isAfter(yearStart) || d.isSame(yearStart, "day"))
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows],
  );

  // Current-month rows for the per-type cards below.
  const monthRows = useMemo(
    () =>
      allRows.filter((r) => {
        const d = getRowDate(r);
        return (
          d &&
          FORM_CODES.includes(r.form_type) &&
          (d.isAfter(monthStart) || d.isSame(monthStart, "day"))
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows],
  );

  // Rows for the weekly view (last 12 weeks).
  const weekRows = useMemo(
    () =>
      allRows.filter((r) => {
        const d = getRowDate(r);
        return (
          d &&
          FORM_CODES.includes(r.form_type) &&
          (d.isAfter(twelveWeeksAgo) || d.isSame(twelveWeeksAgo, "day"))
        );
      }),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [allRows],
  );

  const yearly = useMemo(
    () => buildYearlyTrendCurrentYear(yearRows),
    [yearRows],
  );
  const weekly = useMemo(() => buildWeeklyTrend(weekRows, 12), [weekRows]);
  const daily = useMemo(
    () => buildDailyTrendCurrentMonth(monthRows),
    [monthRows],
  );

  const mainData =
    granularity === "monthly"
      ? yearly
      : granularity === "weekly"
      ? weekly
      : daily;
  const mainTotal = mainData.reduce((s, m) => s + m.total, 0);

  const titleByGranularity = {
    monthly: `${dayjs().format("YYYY")} — Monthly Submissions`,
    weekly: "Last 12 Weeks — Weekly Submissions",
    daily: `${dayjs().format("MMMM YYYY")} — Daily Submissions`,
  };
  const subtitleByGranularity = {
    monthly: "All four form types from January through this month",
    weekly: "Rolling 12-week window, bucketed by week start",
    daily: "All four form types over the current month",
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
      <Card className="lg:col-span-3">
        <SectionTitle
          title={titleByGranularity[granularity]}
          subtitle={subtitleByGranularity[granularity]}
          right={
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
              {TREND_GRANULARITIES.map((g) => {
                const active = granularity === g.key;
                return (
                  <button
                    key={g.key}
                    onClick={() => setGranularity(g.key)}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                      active
                        ? "bg-[#0A0A0A] text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {g.label}
                  </button>
                );
              })}
            </div>
          }
        />
        <div className="flex flex-wrap items-center gap-2 mb-3">
          <span className="text-xs text-gray-500 mr-1">Show:</span>
          {FORM_TYPES.map((f) => {
            const on = visibleTypes.has(f.code);
            return (
              <button
                key={f.code}
                type="button"
                onClick={() => toggleType(f.code)}
                className={`inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium border transition ${
                  on
                    ? "bg-white text-gray-800 border-gray-300"
                    : "bg-gray-50 text-gray-400 border-gray-200 line-through"
                }`}
                title={on ? `Hide ${f.label}` : `Show ${f.label}`}
              >
                <span
                  className="inline-block w-2 h-2 rounded-full"
                  style={{ background: on ? f.color : "#CBD5E1" }}
                />
                {f.label}
              </button>
            );
          })}
          <button
            type="button"
            onClick={() => setVisibleTypes(new Set(FORM_CODES))}
            className="ml-auto text-xs text-gray-500 hover:text-gray-800 underline"
          >
            Reset
          </button>
        </div>
        {mainTotal === 0 ? (
          <EmptyState message="No submissions in this window." />
        ) : visibleTypes.size === 0 ? (
          <EmptyState message="Select at least one form type to view." />
        ) : (
          <div className="h-[340px]">
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={mainData}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="label" fontSize={11} />
                <YAxis allowDecimals={false} fontSize={11} />
                <Tooltip />
                <Legend wrapperStyle={{ fontSize: 12 }} />
                {FORM_TYPES.filter((f) => visibleTypes.has(f.code)).map((f) => (
                  <Line
                    key={f.code}
                    type="monotone"
                    dataKey={f.code}
                    name={f.label}
                    stroke={f.color}
                    strokeWidth={2}
                    dot={{ r: 3, style: { cursor: "pointer" } }}
                    activeDot={{
                      r: 6,
                      style: { cursor: "pointer" },
                      onClick: (_, payload) => {
                        const p = payload?.payload;
                        if (!p?.key) return;
                        setDrilldown({
                          granularity,
                          bucketKey: p.key,
                          formCode: f.code,
                          formLabel: f.label,
                          label: p.label,
                          color: f.color,
                        });
                      },
                    }}
                  />
                ))}
              </LineChart>
            </ResponsiveContainer>
          </div>
        )}
      </Card>

      {FORM_TYPES.map((f) => {
        const series = daily.map((m) => ({ label: m.label, v: m[f.code] }));
        const totalForType = series.reduce((s, x) => s + x.v, 0);
        return (
          <Card key={f.code}>
            <SectionTitle
              title={f.label}
              subtitle={`${totalForType} this month`}
            />
            <div className="h-[180px]">
              <ResponsiveContainer width="100%" height="100%">
                <AreaChart
                  data={series}
                  margin={{ top: 5, right: 10, left: 0, bottom: 0 }}
                >
                  <defs>
                    <linearGradient
                      id={`g-${f.code}`}
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop
                        offset="0%"
                        stopColor={f.color}
                        stopOpacity={0.45}
                      />
                      <stop
                        offset="100%"
                        stopColor={f.color}
                        stopOpacity={0.02}
                      />
                    </linearGradient>
                  </defs>
                  <CartesianGrid strokeDasharray="3 3" />
                  <XAxis
                    dataKey="label"
                    fontSize={10}
                    tickMargin={4}
                    interval="preserveStartEnd"
                  />
                  <YAxis allowDecimals={false} fontSize={10} width={28} />
                  <Tooltip />
                  <Area
                    type="monotone"
                    dataKey="v"
                    name={f.label}
                    stroke={f.color}
                    strokeWidth={2}
                    fill={`url(#g-${f.code})`}
                  />
                </AreaChart>
              </ResponsiveContainer>
            </div>
          </Card>
        );
      })}

      {drilldown && (
        <SubmissionDrilldownModal
          drilldown={drilldown}
          rows={allRows.filter(
            (r) =>
              r.form_type === drilldown.formCode &&
              bucketKeyForRow(r, drilldown.granularity) === drilldown.bucketKey,
          )}
          onClose={() => setDrilldown(null)}
        />
      )}
    </div>
  );
};

const SubmissionDrilldownModal = ({ drilldown, rows, onClose }) => {
  const sorted = [...rows].sort(
    (a, b) => (getRowDate(b)?.valueOf() || 0) - (getRowDate(a)?.valueOf() || 0),
  );
  return (
    <div
      className="fixed inset-0 bg-black/50 flex items-center justify-center z-[9999] px-4"
      onClick={onClose}
    >
      <div
        className="bg-white rounded-xl shadow-lg w-full max-w-lg p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-1">
          <div className="text-left">
            <div className="flex items-center gap-2">
              <span
                className="inline-block w-2.5 h-2.5 rounded-full"
                style={{ background: drilldown.color }}
              />
              <h3 className="text-lg font-semibold text-gray-900">
                {drilldown.formLabel}
              </h3>
            </div>
            <p className="text-sm text-gray-500 mt-0.5">
              {drilldown.label} · {sorted.length} submission
              {sorted.length === 1 ? "" : "s"}
            </p>
          </div>
          <button
            onClick={onClose}
            className="text-gray-400 hover:text-gray-700 text-xl leading-none"
          >
            ×
          </button>
        </div>

        <div className="mt-4 max-h-[50vh] overflow-y-auto divide-y divide-gray-100">
          {sorted.length === 0 ? (
            <div className="text-sm text-gray-500 py-6 text-center">
              No submissions found for this bucket.
            </div>
          ) : (
            sorted.map((r) => {
              const d = getRowDate(r);
              const filler =
                r.observer_name || r.emp_name || r.created_by || "—";
              return (
                <div key={r.id} className="py-3 text-left">
                  <div className="flex items-center justify-between gap-2">
                    <div className="font-medium text-sm text-gray-900 truncate">
                      {filler}
                    </div>
                    <div className="text-[11px] text-gray-500 shrink-0">
                      {d ? d.format("MMM D, YYYY") : "—"}
                    </div>
                  </div>
                  <div className="text-xs text-gray-500 mt-0.5 truncate">
                    {r.company_name ? `Company: ${r.company_name}` : ""}
                    {r.jobDescription ? ` · Job: ${r.jobDescription}` : ""}
                  </div>
                  <div className="text-[11px] text-gray-400 mt-0.5">
                    ID: {r.id}
                    {r.method ? ` · ${r.method}` : ""}
                    {isCompleted(r) ? " · ✓ Completed" : " · Pending"}
                  </div>
                </div>
              );
            })
          )}
        </div>

        <button
          onClick={onClose}
          className="w-full mt-4 text-sm text-gray-500 hover:text-gray-700 py-2"
        >
          Close
        </button>
      </div>
    </div>
  );
};

// ---------------------------------------------------------------------------
// Main dashboard
// ---------------------------------------------------------------------------

const SafetyAnalytics = () => {
  const userDetails = useSelector((state) => state.user.userDetails);
  const [allRows, setAllRows] = useState([]);
  const [loader, setLoader] = useState(false);
  const [activeTab, setActiveTab] = useState("insights");
  const [rangeKey, setRangeKey] = useState("90d");
  const [lastUpdated, setLastUpdated] = useState(null);

  // Custom date range overrides the preset chips. Defaults to last 90 days.
  const [customRange, setCustomRange] = useState({
    startDate: dayjs().subtract(90, "day").toDate(),
    endDate: dayjs().toDate(),
    key: "selection",
  });
  const [showPicker, setShowPicker] = useState(false);
  // Tracks whether the user has explicitly picked a custom range (vs. the
  // automatic update that happens when a preset chip is clicked).
  const [customActive, setCustomActive] = useState(false);
  const pickerRef = useRef(null);

  useEffect(() => {
    const onClick = (e) => {
      if (pickerRef.current && !pickerRef.current.contains(e.target)) {
        setShowPicker(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  // Resolves the active [from, to] window — custom picker wins when active,
  // otherwise we fall back to the selected preset.
  const resolveRange = () => {
    if (customActive) {
      return {
        from: dayjs(customRange.startDate),
        to: dayjs(customRange.endDate),
      };
    }
    const range =
      RANGE_OPTIONS.find((r) => r.key === rangeKey) || RANGE_OPTIONS[0];
    const from =
      range.days != null
        ? dayjs().subtract(range.days, "day")
        : dayjs().startOf("month");
    return { from, to: dayjs() };
  };

  const fetchData = async () => {
    if (!userDetails) return;
    try {
      setLoader(true);
      const { from: rangeStart, to: rangeEnd } = resolveRange();
      // Custom range: send the user-picked window verbatim so the API filters
      // to exactly that period. Presets still extend to year-start so the
      // Trends tab can show YTD context.
      let from = rangeStart;
      if (!customActive) {
        const yearStart = dayjs().startOf("year");
        if (yearStart.isBefore(rangeStart)) from = yearStart;
      }

      const params = {
        from: from.format("YYYY-MM-DD"),
        to: rangeEnd.format("YYYY-MM-DD"),
        form_types: FORM_CODES.join(","),
      };
      if (userDetails.role === 3) params.pm_id = userDetails.id;

      const resp = await api.get("/v1/forms/analytics", { params });

      if (resp.status === 200) {
        setAllRows(Array.isArray(resp.data) ? resp.data : []);
        setLastUpdated(dayjs());
      } else {
        setAllRows([]);
      }
    } catch (err) {
      console.error("Safety analytics fetch failed", err);
      setAllRows([]);
    } finally {
      setLoader(false);
    }
  };

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    userDetails,
    rangeKey,
    customActive,
    customRange.startDate,
    customRange.endDate,
  ]);

  // Filter to the 4 supported form types and the active date range.
  const rows = useMemo(() => {
    const { from, to } = resolveRange();
    return allRows.filter((r) => {
      if (!FORM_CODES.includes(r.form_type)) return false;
      const d = getRowDate(r);
      if (!d) return false;
      return (
        (d.isAfter(from) || d.isSame(from, "day")) &&
        (d.isBefore(to) || d.isSame(to, "day"))
      );
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allRows,
    rangeKey,
    customActive,
    customRange.startDate,
    customRange.endDate,
  ]);

  // Top stat-cards: counts per form type for the selected range,
  // compared against the immediately preceding window of equal length.
  const stats = useMemo(() => {
    const { from, to } = resolveRange();
    const windowDays = Math.max(1, to.diff(from, "day") + 1);
    const prevTo = from.subtract(1, "day");
    const prevFrom = prevTo.subtract(windowDays - 1, "day");

    const inWindow = (d, a, b) =>
      d &&
      (d.isAfter(a) || d.isSame(a, "day")) &&
      (d.isBefore(b) || d.isSame(b, "day"));

    const out = {};
    FORM_CODES.forEach((c) => {
      const inThis = allRows.filter(
        (r) => r.form_type === c && inWindow(getRowDate(r), from, to),
      ).length;
      const inLast = allRows.filter(
        (r) => r.form_type === c && inWindow(getRowDate(r), prevFrom, prevTo),
      ).length;
      const trend =
        inLast === 0 ? null : Math.round(((inThis - inLast) / inLast) * 100);
      out[c] = { current: inThis, prev: inLast, trend };
    });
    return out;
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    allRows,
    rangeKey,
    customActive,
    customRange.startDate,
    customRange.endDate,
  ]);

  const rangeLabel = useMemo(() => {
    if (customActive) return "selected range";
    const r = RANGE_OPTIONS.find((o) => o.key === rangeKey);
    return (r?.label || "range").toLowerCase();
  }, [rangeKey, customActive]);

  return (
    <SideNavLayout>
      <div
        className="p-2 max-h-[90vh] overflow-y-auto space-y-4"
        style={{ scrollbarWidth: "none" }}
      >
        {/* Header */}
        <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
          <div className="text-left">
            <h1 className="text-xl font-semibold text-[#0A0A0A]">
              Safety Analytics Dashboard
            </h1>
            <p className="text-sm text-[#717182]">
              Real-time metrics across BBS, Safety &amp; Health, Driver Log and
              Accident reports.
            </p>
          </div>
          <div className="flex flex-wrap items-center gap-2">
            <div className="inline-flex rounded-lg border border-gray-200 bg-white p-1 shadow-sm">
              {RANGE_OPTIONS.map((r) => {
                const active = !customActive && rangeKey === r.key;
                return (
                  <button
                    key={r.key}
                    onClick={() => {
                      setCustomActive(false);
                      setRangeKey(r.key);
                    }}
                    className={`px-3 py-1 text-xs font-medium rounded-md transition ${
                      active
                        ? "bg-[#0A0A0A] text-white"
                        : "text-gray-600 hover:bg-gray-100"
                    }`}
                  >
                    {r.label}
                  </button>
                );
              })}
            </div>

            {/* Custom range picker */}
            <div className="relative" ref={pickerRef}>
              <button
                onClick={() => setShowPicker((v) => !v)}
                className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs font-medium border rounded-lg shadow-sm transition ${
                  customActive
                    ? "bg-[#0A0A0A] text-white border-[#0A0A0A]"
                    : "bg-white text-gray-700 border-gray-200 hover:bg-gray-50"
                }`}
                title="Custom date range"
              >
                <CalendarTodayIcon style={{ fontSize: 14 }} />
                {customActive
                  ? `${dayjs(customRange.startDate).format(
                      "MMM D, YYYY",
                    )} – ${dayjs(customRange.endDate).format("MMM D, YYYY")}`
                  : "Custom Range"}
              </button>
              {showPicker && (
                <div className="absolute right-0 mt-2 z-[9999] shadow-lg border rounded-md bg-white">
                  <DateRangePicker
                    onChange={(item) => setCustomRange(item.selection)}
                    ranges={[customRange]}
                    moveRangeOnFirstSelection={false}
                    maxDate={new Date()}
                  />
                  <div className="flex justify-end gap-2 p-3 border-t bg-gray-50">
                    <button
                      onClick={() => {
                        setCustomActive(false);
                        setCustomRange({
                          startDate: dayjs().subtract(90, "day").toDate(),
                          endDate: dayjs().toDate(),
                          key: "selection",
                        });
                        setRangeKey("90d");
                        setShowPicker(false);
                      }}
                      className="px-3 py-1 text-sm border rounded-md text-gray-600 hover:bg-gray-100"
                    >
                      Reset
                    </button>
                    <button
                      onClick={() => {
                        setCustomActive(true);
                        setShowPicker(false);
                      }}
                      className="px-3 py-1 text-sm rounded-md bg-[#0A0A0A] text-white hover:bg-gray-800"
                    >
                      Apply
                    </button>
                  </div>
                </div>
              )}
            </div>
            <button
              onClick={fetchData}
              className="inline-flex items-center gap-1 px-3 py-1.5 text-xs font-medium border border-gray-200 rounded-lg bg-white shadow-sm hover:bg-gray-50"
              title="Refresh"
            >
              <RefreshIcon style={{ fontSize: 16 }} />
              {lastUpdated
                ? `Updated ${lastUpdated.format("HH:mm")}`
                : "Refresh"}
            </button>
          </div>
        </div>

        {/* Stat cards (per form type, this month) */}
        <div className="flex flex-wrap gap-3">
          {FORM_TYPES.map((f) => {
            const s = stats[f.code] || {};
            return (
              <StatCard
                key={f.code}
                label={f.label}
                value={s.current ?? 0}
                sub={
                  s.prev ? `vs ${s.prev} prior period` : `no prior-period data`
                }
                accent={f.color}
                trend={s.trend}
                Icon={
                  f.code === "AIF"
                    ? WarningAmberIcon
                    : f.code === "BBS"
                    ? AssignmentTurnedInIcon
                    : f.code === "WDS"
                    ? GroupIcon
                    : BusinessIcon
                }
              />
            );
          })}
        </div>

        {/* Tabs */}
        <div className="w-full flex flex-wrap rounded-2xl bg-[#DBEAFE] p-1">
          {TABS.map(({ key, label, Icon }) => {
            const active = activeTab === key;
            return (
              <button
                key={key}
                onClick={() => setActiveTab(key)}
                className={`flex-1 min-w-[140px] flex items-center justify-center gap-2 px-4 py-2 text-sm font-medium rounded-2xl transition ${
                  active
                    ? "bg-white text-[#0A0A0A] shadow-sm"
                    : "bg-transparent text-[#0A0A0A]/70 hover:text-[#0A0A0A]"
                }`}
              >
                <Icon style={{ fontSize: 18 }} />
                {label}
              </button>
            );
          })}
        </div>

        {/* Tab body */}
        <div>
          {activeTab === "insights" && <KeyInsights rows={rows} />}
          {activeTab === "incidents" && <Incidents rows={rows} />}
          {activeTab === "bbs" && <BbsData rows={rows} />}
          {activeTab === "driverlog" && <DriverLogData rows={rows} />}
          {activeTab === "trends" && <Trends allRows={allRows} />}
          {activeTab === "kpis" && <HseStatsContent embedded />}
        </div>

        {rows.length === 0 && !loader && (
          <Card>
            <div className="text-center py-6">
              <HourglassEmptyIcon
                style={{ fontSize: 28 }}
                className="text-gray-400 mb-2"
              />
              <p className="text-sm text-gray-600">
                No submissions found in the selected time range. Try widening
                the range or check whether forms have been filed for BBS, SHP,
                WDS or AIF.
              </p>
            </div>
          </Card>
        )}
      </div>

      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={loader}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
    </SideNavLayout>
  );
};

export default SafetyAnalytics;
